<template>
  <v-card
    dark
    class="coherence-semantics-card d-flex flex-column"
    :class="{
      'coherence-semantics-card--full': !embedded,
      'coherence-semantics-card--compact-lyrics': compactLyricsViewport,
    }"
    elevation="2"
  >
    <v-card-title
      class="py-2 px-3 subtitle-2 font-weight-bold d-flex align-center flex-shrink-0 semantics-card-title-row"
    >
      <v-icon left small color="white" class="flex-shrink-0">mdi-shield-alert</v-icon>
      <span class="semantics-card-title-text">NEXT Semantica</span>
      <v-spacer />
      <v-btn
        icon
        small
        aria-label="Expand or collapse NEXT Semantica panel"
        @click="$emit('update:collapsed', !collapsed)"
      >
        <v-icon small>{{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
      </v-btn>
      <v-menu offset-y left :close-on-content-click="false">
        <template #activator="{ on, attrs }">
          <v-btn icon small v-bind="attrs" v-on="on" aria-label="Open semantic settings">
            <v-icon small>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-card class="semantics-options-menu pa-3" width="320" min-width="320" max-width="320">
          <div class="d-flex align-center subtitle-2 font-weight-bold mb-2">
            <v-icon small class="mr-2">mdi-tune-variant</v-icon>
            <span>Options</span>
          </div>
          <v-divider class="mb-2" />
          <div class="d-flex align-center px-1 py-1">
            <div class="subtitle-2 font-weight-medium">Group by</div>
            <v-spacer />
            <v-switch
              v-model="groupByEnabled"
              inset
              dense
              hide-details
              class="mt-0 pt-0"
              aria-label="Enable grouping options"
            />
          </div>
          <div v-if="groupByEnabled" class="px-1 pb-1">
            <v-select
              v-model="scoreTab"
              :items="scoreModeOptions"
              item-text="text"
              item-value="value"
              label="Score Mode"
              dense
              outlined
              hide-details="auto"
            />
          </div>
          <v-divider class="my-3" />
          <div class="px-1">
            <div class="subtitle-2 font-weight-medium mb-1 semantics-text-size-title">Text size</div>
            <div class="d-flex align-center semantics-text-size-control">
              <span class="semantics-text-size-a semantics-text-size-a--small">A</span>
              <v-slider
                v-model="fontSizePxTab"
                dense
                color="primary"
                :min="12"
                :max="24"
                :step="2"
                thumb-label="always"
                hide-details
                class="mx-2 flex-grow-1"
                aria-label="NEXT Semantica text size"
              >
                <template #thumb-label="{ value }">
                  {{ Number(value) }}px
                </template>
              </v-slider>
              <span class="semantics-text-size-a semantics-text-size-a--large">A</span>
            </div>
          </div>
        </v-card>
      </v-menu>
      <span
        v-if="!compactLyricsViewport"
        class="caption semantics-card-header-meta semantics-card-meta--ellipsis"
      >
        {{ semanticsMetaLabel }}
      </span>
    </v-card-title>

    <div v-if="!collapsed" class="px-3 pb-2" :class="compactLyricsViewport ? 'pt-0' : 'pt-1'">
      <div class="coherence-bar-wrap" :class="compactLyricsViewport ? 'mt-1' : 'mt-3'">
        <div class="coherence-bar">
          <div class="coherence-base"></div>
          <div class="coherence-gradient"></div>
          <transition name="coherence-indicator-fade">
            <div
              v-if="showCoherenceIndicator"
              class="bar-indicator"
              :style="{ left: `${indicatorLeftPercent}%` }"
            >
              <div class="indicator-pill" :class="indicatorPillClass">{{ displayCoherence }}%</div>
            </div>
          </transition>
        </div>
        <div class="bar-labels d-flex justify-space-between align-center mt-1">
          <span class="label-end">100%</span>
          <span class="label-mid">50%</span>
          <span class="label-end">0%</span>
        </div>
      </div>
    </div>

    <v-divider v-if="!collapsed" />

    <v-card-text
      v-if="!collapsed"
      class="d-flex flex-column lyrics-card-text"
      :class="compactLyricsViewport ? 'semantics-card-text--compact-lyrics' : 'flex-grow-1'"
      style="min-height: 0"
    >
      <div class="semantics-scroll-shell">

        <div class="semantics-scroll-fade semantics-scroll-fade--top" aria-hidden="true"></div>
        <div class="semantics-scroll-fade semantics-scroll-fade--bottom" aria-hidden="true"></div>
        <div
          ref="segmentsScroll"
          class="semantics-scroll semantics-scroll--lyrics"
          :style="compactLyricsScrollStyle"
        >
          <div v-if="!lyricsLines.length" class="lyrics-empty caption pa-6 text-center">
            No timed transcript for this analysis.
          </div>
          <div v-else class="lyrics-panel" :style="lyricsPanelStyle">
            <div class="lyrics-edge-spacer" :style="lyricsEdgeSpacerStyle" aria-hidden="true"></div>
            <div
              v-for="(line, idx) in lyricsLines"
              :key="`ly-${line.lineKey}`"
              ref="lyricsLineEl"
              class="lyrics-line-wrap"
              role="button"
              tabindex="0"
              @keydown.enter.prevent="seekToLyricsLine(line)"
              @keydown.space.prevent="seekToLyricsLine(line)"
            >
              <div v-if="line.segmentKey && line.alertBlock" class="lyrics-segment-card" :class="[`lyrics-segment-card--${alertLineTone(line.alertBlock)}`, { 'lyrics-segment-card--active': isActiveBlock(line.alertBlock), 'lyrics-segment-card--below-threshold': blockBelowThreshold(line.alertBlock), 'lyrics-segment-card--labeled-deceptive': blockLabel(line.alertBlock) && blockLabel(line.alertBlock).is_incoherent === true, 'lyrics-segment-card--labeled-truthful': blockLabel(line.alertBlock) && blockLabel(line.alertBlock).is_incoherent === false }]">
                <div class="lyrics-segment-card__header">
                  <v-tooltip bottom max-width="280">
                    <template #activator="{ on, attrs }">
                      <div
                        class="segment-score-circle"
                        :class="`segment-score-circle--${toneFromCoherencePercent(blockScore(line.alertBlock))}`"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ formatAggregateCoherence(blockScore(line.alertBlock)) }}
                      </div>
                    </template>
                    <div class="caption">
                      <div><strong>Coherence:</strong> {{ formatAggregateCoherence(blockScore(line.alertBlock)) }}</div>
                      <div v-if="scoreTab === 'computed_subtraction'">Mode: Alerts subtraction</div>
                      <div v-else>Mode: Alert major</div>
                    </div>
                  </v-tooltip>
                  <v-tooltip v-if="blockBelowThreshold(line.alertBlock)" bottom max-width="300">
                    <template #activator="{ on, attrs }">
                      <v-icon
                        x-small
                        class="ml-1 segment-below-threshold-flag"
                        color="amber"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-flag-variant</v-icon>
                    </template>
                    <span>{{ blockBelowThresholdTooltip(line.alertBlock) }}</span>
                  </v-tooltip>
                  <v-spacer />
                  <span class="lyrics-segment-card__speaker" :title="line.alertBlock.speaker || ''">
                    {{ blockSpeakerDisplay(line.alertBlock) }}
                  </span>
                  <v-spacer />
                  <v-btn
                    icon
                    x-small
                    aria-label="Open segment details"
                    @click.stop="openBlockModal(line.alertBlock)"
                  >
                    <v-icon x-small>mdi-information-outline</v-icon>
                  </v-btn>
                </div>
                <div
                  class="lyrics-line"
                  :class="lyricsLineClass(idx)"
                  @click.self="seekToLyricsLine(line)"
                >
                  <span
                    v-for="word in line.words"
                    :key="`lw-${word.word_index}`"
                    :data-word-index="String(word.word_index)"
                    class="words-live-token words-live-token--lyrics"
                    :class="[
                      wordTokenClass(word.word_index),
                      {
                        'words-live-token--in-segment-card': true,
                        'words-live-token--anchor-with-pill': hasAlertPillsForWord(
                          word.word_index,
                          line.alertBlock,
                          line
                        ),
                      },
                      {
                        'words-live-token--pending': !isWordRevealed(word),
                      },
                      { 'words-live-token--range-hover': isWordInHoveredAlertRange(word.word_index) },
                      { 'words-live-token--spoken': isWordRevealed(word) },
                    ]"
                    :style="mergedWordTokenStyle(word.word_index, line.alertBlock, line)"
                    role="button"
                    tabindex="0"
                    :title="wordSeekTitle(word)"
                    @mouseenter="onWordHoverStart(word.word_index)"
                    @mouseleave="onWordHoverEnd"
                    @focus="onWordHoverStart(word.word_index)"
                    @blur="onWordHoverEnd"
                    @click.stop="onWordActivate(word)"
                    @keydown.enter.prevent.stop="onWordActivate(word)"
                    @keydown.space.prevent.stop="onWordActivate(word)"
                  >
                    <template v-if="hasAlertPillsForWord(word.word_index, line.alertBlock, line)">
                      <span
                        class="words-live-token__anchor-text"
                        :style="anchorUnderlineStyle(word.word_index, line.alertBlock, line)"
                      >{{ word.text }}</span>
                      <span
                        v-for="(pillAlert, pidx) in alertPillsForWord(word.word_index, line.alertBlock, line)"
                        :key="`pill-card-${word.word_index}-${pillAlert.alertId || pidx}`"
                        class="anchor-coherence-pill"
                        :class="`anchor-coherence-pill--${alertPillTone(pillAlert)}`"
                        :style="alertPillStyle(pillAlert)"
                        :title="alertPillTitle(pillAlert)"
                        role="button"
                        tabindex="0"
                        aria-label="Open coherence alert details"
                        @click.stop="onAlertPillActivate(word.word_index, pillAlert, line.alertBlock)"
                        @keydown.enter.prevent.stop="onAlertPillActivate(word.word_index, pillAlert, line.alertBlock)"
                        @keydown.space.prevent.stop="onAlertPillActivate(word.word_index, pillAlert, line.alertBlock)"
                      >
                        <span class="anchor-coherence-pill__value">{{ alertPillDigits(pillAlert) }}</span>
                        <v-icon x-small class="anchor-coherence-pill__info">mdi-information-outline</v-icon>
                      </span>
                    </template>
                    <template v-else>{{ word.text }}</template>
                  </span>
                </div>
                <div
                  v-if="$vuetify.breakpoint.mdAndUp"
                  class="lyrics-segment-card__footer debug-actions"
                >
                  <v-btn
                    x-small
                    depressed
                    :loading="labelsSaving[line.alertBlock.block_id || line.alertBlock.blockId]"
                    :color="blockLabel(line.alertBlock) && blockLabel(line.alertBlock).is_incoherent === true ? 'success' : 'transparent'"
                    :class="['label-btn', blockLabel(line.alertBlock) && blockLabel(line.alertBlock).is_incoherent === true ? 'label-btn--active-correct' : 'label-btn--inactive']"
                    @click.stop="toggleLabel(line.alertBlock, true)"
                  >
                    <v-icon x-small left>mdi-check</v-icon>
                    Correct
                  </v-btn>
                  <v-btn
                    x-small
                    depressed
                    :loading="labelsSaving[line.alertBlock.block_id || line.alertBlock.blockId]"
                    :color="blockLabel(line.alertBlock) && blockLabel(line.alertBlock).is_incoherent === false ? 'error' : 'transparent'"
                    :class="['label-btn', blockLabel(line.alertBlock) && blockLabel(line.alertBlock).is_incoherent === false ? 'label-btn--active-incorrect' : 'label-btn--inactive']"
                    @click.stop="toggleLabel(line.alertBlock, false)"
                  >
                    <v-icon x-small left>mdi-close</v-icon>
                    Incorrect
                  </v-btn>
                </div>
              </div>
              <div
                v-else
                class="lyrics-plain-line-wrap"
                :class="{ 'lyrics-plain-line-wrap--fn': fnLabels[fnLabelKey(line)] }"
                @mouseenter="hoveredPlainLineKey = fnLabelKey(line)"
                @mouseleave="hoveredPlainLineKey = null"
              >
                <div
                  class="lyrics-line"
                  :class="lyricsLineClass(idx)"
                  @click.self="seekToLyricsLine(line)"
                >
                <span
                  v-for="word in line.words"
                  :key="`lw-plain-${word.word_index}`"
                  :data-word-index="String(word.word_index)"
                  class="words-live-token words-live-token--lyrics"
                  :class="[
                    wordTokenClass(word.word_index),
                    {
                      'words-live-token--anchor-with-pill': hasAlertPillsForWord(
                        word.word_index,
                        line.alertBlock,
                        line
                      ),
                      'words-live-token--pending': !isWordRevealed(word),
                    },
                    { 'words-live-token--range-hover': isWordInHoveredAlertRange(word.word_index) },
                    { 'words-live-token--spoken': isWordRevealed(word) },
                  ]"
                  :style="mergedWordTokenStyle(word.word_index, line.alertBlock, line)"
                  role="button"
                  tabindex="0"
                  :title="wordSeekTitle(word)"
                  @mouseenter="onWordHoverStart(word.word_index)"
                  @mouseleave="onWordHoverEnd"
                  @focus="onWordHoverStart(word.word_index)"
                  @blur="onWordHoverEnd"
                  @click.stop="onWordActivate(word)"
                  @keydown.enter.prevent.stop="onWordActivate(word)"
                  @keydown.space.prevent.stop="onWordActivate(word)"
                >
                  <template v-if="hasAlertPillsForWord(word.word_index, line.alertBlock, line)">
                    <span
                      class="words-live-token__anchor-text"
                      :style="anchorUnderlineStyle(word.word_index, line.alertBlock, line)"
                    >{{ word.text }}</span>
                    <span
                      v-for="(pillAlert, pidx) in alertPillsForWord(word.word_index, line.alertBlock, line)"
                      :key="`pill-plain-${word.word_index}-${pillAlert.alertId || pidx}`"
                      class="anchor-coherence-pill"
                      :class="`anchor-coherence-pill--${alertPillTone(pillAlert)}`"
                      :style="alertPillStyle(pillAlert)"
                      :title="alertPillTitle(pillAlert)"
                      role="button"
                      tabindex="0"
                      aria-label="Open coherence alert details"
                      @click.stop="onAlertPillActivate(word.word_index, pillAlert, line.alertBlock)"
                      @keydown.enter.prevent.stop="onAlertPillActivate(word.word_index, pillAlert, line.alertBlock)"
                      @keydown.space.prevent.stop="onAlertPillActivate(word.word_index, pillAlert, line.alertBlock)"
                    >
                      <span class="anchor-coherence-pill__value">{{ alertPillDigits(pillAlert) }}</span>
                      <v-icon x-small class="anchor-coherence-pill__info">mdi-information-outline</v-icon>
                    </span>
                  </template>
                  <template v-else>{{ word.text }}</template>
                </span>
                </div>
                <transition name="fn-btn-fade">
                  <div
                    v-if="$vuetify.breakpoint.mdAndUp && (hoveredPlainLineKey === fnLabelKey(line) || fnLabels[fnLabelKey(line)])"
                    class="fn-label-row"
                  >
                    <v-btn
                      x-small
                      depressed
                      :loading="fnLabelsSaving[fnLabelKey(line)]"
                      :color="fnLabels[fnLabelKey(line)] ? 'error' : 'transparent'"
                      :class="['label-btn', fnLabels[fnLabelKey(line)] ? 'label-btn--active-incorrect' : 'label-btn--inactive']"
                      @click.stop="toggleFNLabel(line)"
                    >
                      <v-icon x-small left>{{ fnLabels[fnLabelKey(line)] ? 'mdi-flag' : 'mdi-flag-outline' }}</v-icon>
                      {{ fnLabels[fnLabelKey(line)] ? 'Missed deception (FN)' : 'Mark as missed deception' }}
                    </v-btn>
                  </div>
                </transition>
              </div>
            </div>
            <div class="lyrics-edge-spacer" :style="lyricsEdgeSpacerStyle" aria-hidden="true"></div>
          </div>
        </div>
      </div>
    </v-card-text>

    <template v-if="!collapsed && noSemanticsAlerts.length">
      <v-divider />
      <div class="no-semantics-strip px-3 py-2">
        <div class="caption font-weight-bold">
          Alerts without semantics · {{ noSemanticsAlerts.length }}
        </div>
        <div class="caption no-semantics-strip__hint">
          Raised in a pause between ideas: there is no idea text to compare them with, so they
          subtract nothing.
        </div>
        <div class="d-flex flex-wrap no-semantics-strip__chips">
          <v-chip
            v-for="a in noSemanticsAlerts"
            :key="`nosem-${a.alert_id || a.alertId}`"
            x-small
            outlined
            color="grey lighten-1"
            class="mr-1 mt-1"
            :title="`${a.gesture_name || a.rule_id || 'Rule'} — open details`"
            @click="openNoSemanticsAlert(a)"
          >
            {{ formatMsClock(a.gesture_timestamp_ms) }} ·
            {{ formatAlertFieldDisplay(alertGestureName(a)) }}
          </v-chip>
        </div>
      </div>
    </template>

    <v-divider v-if="showIncoherenceFooter" />
    <div v-if="showIncoherenceFooter" class="semantics-panel-footer pa-2">
      <v-spacer />
      <v-tooltip v-if="incoherencePickActive" top>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            small
            color="warning"
            :disabled="incoherenceBusy"
            aria-label="Cancel lie labeling"
            v-bind="attrs"
            v-on="on"
            @click="$emit('incoherence-pick-cancel')"
          >
            <v-icon small color="white">mdi-close</v-icon>
          </v-btn>
        </template>
        <span>{{ incoherenceButtonTooltip }}</span>
      </v-tooltip>
      <v-menu v-else top left offset-y>
        <template #activator="{ on: menuOn, attrs: menuAttrs }">
          <v-tooltip top>
            <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <v-btn
                icon
                small
                :color="incoherenceButtonColor"
                :disabled="incoherenceBusy"
                aria-label="Lie label options"
                v-bind="{ ...menuAttrs, ...tooltipAttrs }"
                v-on="{ ...menuOn, ...tooltipOn }"
              >
                <!-- Labeled states inherit the button color (red = lie, green = truthful). -->
                <v-icon small :color="incoherenceHasLabel ? '' : 'white'">
                  {{ incoherenceIsTruthful ? 'mdi-check-decagram' : 'mdi-drama-masks' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ incoherenceButtonTooltip }}</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-list-item @click="$emit('incoherence-edit-start')">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-pencil</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ incoherenceRangeMs ? 'Edit lie range' : 'Label the lie' }}</v-list-item-title>
              <v-list-item-subtitle v-if="incoherenceRangeMs">
                {{ formatMsClock(incoherenceRangeMs.startMs) }} - {{ formatMsClock(incoherenceRangeMs.endMs) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item :disabled="incoherenceIsTruthful" @click="onMarkTruthfulClick">
            <v-list-item-icon class="mr-2">
              <v-icon small :color="incoherenceIsTruthful ? undefined : 'success'">mdi-check-decagram</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Mark video as truthful</v-list-item-title>
              <v-list-item-subtitle v-if="incoherenceIsTruthful">Video labeled as truthful</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item :disabled="!incoherenceHasLabel" @click="incoherenceClearConfirmOpen = true">
            <v-list-item-icon class="mr-2">
              <v-icon small :color="incoherenceHasLabel ? 'error' : undefined">mdi-delete-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Clear label…</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-dialog v-model="blockModalOpen" max-width="640" scrollable>
      <v-card v-if="modalBlock" light class="semantics-dialog-card">
        <v-card-title class="subtitle-1 font-weight-bold pr-8">
          Segment alerts
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="ml-1"
                aria-label="About alert fields"
                v-bind="attrs"
                v-on="on"
                @click.stop="alertFieldsHelpOpen = true"
              >
                <v-icon small color="grey lighten-1">mdi-information-outline</v-icon>
              </v-btn>
            </template>
            <span>What do these fields mean?</span>
          </v-tooltip>
          <v-spacer />
          <v-btn icon small @click="closeBlockModal"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle class="pb-0 pt-1">{{ modalBlock.text || '—' }}</v-card-subtitle>
        <v-card-text class="pt-4">
          <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px;">
            <v-chip x-small outlined>{{ formatMsClock(modalBlock.start_time_ms) }}</v-chip>
            <span class="caption grey--text">to</span>
            <v-chip x-small outlined>{{ formatMsClock(modalBlock.end_time_ms) }}</v-chip>
            <v-chip x-small dark class="font-weight-bold" :color="coherenceVuetifyColor(blockScore(modalBlock))">
              Coherence {{ formatAggregateCoherence(blockScore(modalBlock)) }}
            </v-chip>
          </div>
          <div v-if="!modalResolvedAlerts.length" class="body-2 grey--text">
            No coherence alerts are linked to this segment.
          </div>
          <div v-for="(a, ai) in modalResolvedAlerts" :key="a.alert_id || ai" class="mb-3">
            <v-card outlined light class="semantics-dialog-alert-card">
              <v-card-text class="py-2">
                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                  <div class="subtitle-2 font-weight-bold">{{ a.gesture_name || a.rule_id || 'Rule' }}</div>
                  <v-chip
                    x-small
                    dark
                    class="font-weight-bold"
                    :color="coherenceVuetifyColor(Number(a.coherence_after))"
                  >
                    {{ formatAlertSubtractionChip(a) }}
                  </v-chip>
                  <v-chip
                    v-if="alertDidNotSubtract(a)"
                    x-small
                    outlined
                    color="grey darken-1"
                  ><v-icon x-small left>mdi-minus-circle-outline</v-icon>Not subtracting</v-chip>
                  <v-chip
                    v-if="alertVerdicts[String(a.alert_id || a.alertId)] === 'correct'"
                    x-small
                    dark
                    color="success"
                  ><v-icon x-small left>mdi-check</v-icon>Correct</v-chip>
                  <v-chip
                    v-else-if="alertVerdicts[String(a.alert_id || a.alertId)] === 'incorrect'"
                    x-small
                    dark
                    color="error"
                  ><v-icon x-small left>mdi-close</v-icon>Incorrect</v-chip>
                  <v-spacer />
                  <v-btn x-small text color="primary" @click.stop="seekToAlert(a)">
                    <v-icon left x-small>mdi-play-circle-outline</v-icon>
                    Jump
                  </v-btn>
                </div>
                <div class="caption grey--text mt-2">{{ formatMsClock(a.gesture_timestamp_ms) }}</div>
                <div v-if="a.explanation" class="body-2 mt-2">{{ a.explanation }}</div>

                <v-divider class="my-3" />

                <div class="alert-detail-grid caption">
                  <div v-if="alertDidNotSubtract(a)" class="alert-detail-row">
                    <span class="alert-detail-label">Why no subtraction</span>
                    <span class="alert-detail-value">{{ reasonCategoryLabel(alertReasonCategory(a)) }}</span>
                  </div>
                  <div
                    v-if="
                      alertDidNotSubtract(a) &&
                      alertReasonCategory(a) !== 'no_semantics' &&
                      alertPotentialReductionPct(a) != null
                    "
                    class="alert-detail-row"
                  >
                    <span class="alert-detail-label">Would subtract</span>
                    <span class="alert-detail-value">-{{ alertPotentialReductionPct(a) }}% (not applied)</span>
                  </div>
                  <div class="alert-detail-row">
                    <span class="alert-detail-label">Gesture</span>
                    <span class="alert-detail-value">{{ formatAlertFieldDisplay(alertGestureName(a)) }}</span>
                  </div>
                  <div class="alert-detail-row">
                    <span class="alert-detail-label">Rule value</span>
                    <span class="alert-detail-value">{{ formatAlertFieldDisplay(alertMetadataSourceValue(a)) }}</span>
                  </div>
                  <div class="alert-detail-row">
                    <span class="alert-detail-label">Time window</span>
                    <span class="alert-detail-value">{{ formatAlertWindowSecondsDisplay(a) }}</span>
                  </div>
                  <div class="alert-detail-row">
                    <span class="alert-detail-label">Matched speaker</span>
                    <span class="alert-detail-value">{{ formatAlertFieldDisplay(alertSemanticSpeaker(a)) }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="alertFieldsHelpOpen" max-width="520" @click:outside="alertFieldsHelpOpen = false">
      <v-card light class="semantics-dialog-card">
        <v-card-title class="subtitle-1 font-weight-bold pr-10">
          Alert field guide
          <v-spacer />
          <v-btn icon small @click="alertFieldsHelpOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pb-4">
          <p class="body-2 grey--text mb-4">
            Short reference for the details shown on each alert card.
          </p>
          <div
            v-for="(item, hi) in alertFieldHelpEntries"
            :key="'help-' + hi"
            class="alert-help-block mb-4"
          >
            <div class="subtitle-2 font-weight-bold">{{ item.title }}</div>
            <div class="body-2 mt-1">{{ item.body }}</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="incoherenceClearConfirmOpen" max-width="380">
      <v-card light class="semantics-dialog-card">
        <v-card-title class="subtitle-1 font-weight-bold">Clear label?</v-card-title>
        <v-card-text class="body-2">
          <template v-if="incoherenceRangeMs">
            The labeled lie range ({{ formatMsClock(incoherenceRangeMs.startMs) }} -
            {{ formatMsClock(incoherenceRangeMs.endMs) }}) will be removed from this video.
          </template>
          <template v-else-if="incoherenceIsTruthful">
            The truthful label will be removed from this video.
          </template>
          The video will not count in the evaluation metrics until it is labeled again
          (lie range or truthful).
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="incoherenceClearConfirmOpen = false">Cancel</v-btn>
          <v-btn color="error" text :disabled="incoherenceBusy" @click="confirmIncoherenceClear">Clear label</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation before a truthful mark replaces an existing lie range. -->
    <v-dialog v-model="incoherenceTruthfulConfirmOpen" max-width="380">
      <v-card light class="semantics-dialog-card">
        <v-card-title class="subtitle-1 font-weight-bold">Mark video as truthful?</v-card-title>
        <v-card-text class="body-2">
          <template v-if="incoherenceRangeMs">
            The labeled lie range ({{ formatMsClock(incoherenceRangeMs.startMs) }} -
            {{ formatMsClock(incoherenceRangeMs.endMs) }}) will be replaced by a truthful label
            (no lie in this video).
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="incoherenceTruthfulConfirmOpen = false">Cancel</v-btn>
          <v-btn color="success" text :disabled="incoherenceBusy" @click="confirmIncoherenceTruthful">
            Mark truthful
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AlertFeedbackDialog
      v-model="feedbackDialogOpen"
      mode="idea_alert"
      :source-type="isManualAnalysis ? 'manual_analysis' : 'interview'"
      :source-id="sourceId"
      :idea-id="feedbackIdeaId"
      :alerts="feedbackAlerts"
      @saved="onFeedbackSaved"
    />
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import {
  semanticBlocksForSegmentStrategy,
  blockScoreForCoherenceMode,
  normalizeSegmentationTab,
} from '@/utils/coherenceSemanticsTimeline';
import { currentGestureName } from '@/utils/gestureLabels';
import CoherenceLabelService from '@/services/CoherenceLabelService';
import DetectionFeedbackService from '@/services/DetectionFeedbackService';
import { parseAnalysisKey } from '@/store/modules/analysisData';
import AlertFeedbackDialog from '@/components/analysis/AlertFeedbackDialog.vue';

export default {
  name: 'CoherenceSemanticsPanel',

  components: { AlertFeedbackDialog },

  props: {
    semanticModes: {
      type: Object,
      default: null,
    },
    coherenceAlerts: {
      type: Array,
      default: () => [],
    },
    /**
     * Store key of the analysis: composite analysisKey '<sourceType>:<sourceId>:<runId|active>'
     * (M2) or a plain numeric id (legacy callers). API calls use the parsed `sourceId` / `runId`.
     */
    interviewId: {
      type: [Number, String],
      default: null,
    },
    currentVideoTime: {
      type: Number,
      default: 0,
    },
    embedded: {
      type: Boolean,
      default: true,
    },
    /** 'idea' | 'none' (grouping off); legacy 'merged' / 'per_alert' read as 'idea'. */
    segmentationTab: {
      type: String,
      default: 'idea',
    },
    scoreMode: {
      type: String,
      default: 'computed_subtraction',
    },
    fontScale: {
      type: Number,
      default: 20 / 24,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    isManualAnalysis: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, the main lyrics scroll uses the same list as desktop but is capped to four
     * text lines tall (e.g. mobile stack above the video).
     */
    compactLyricsViewport: {
      type: Boolean,
      default: false,
    },
    /** From analysis_config_snapshot.engines.coherence (defaults match legacy UI). */
    semaphoreThresholdGood: {
      type: Number,
      default: 80,
    },
    semaphoreThresholdWarn: {
      type: Number,
      default: 50,
    },
    /** Enables the incoherence-labeling footer button (desktop analysis screen only, not shared views). */
    incoherenceControlsEnabled: {
      type: Boolean,
      default: false,
    },
    /** Labeled lie range ({ startMs, endMs }) or null when the video has no lie label. */
    incoherenceRangeMs: {
      type: Object,
      default: null,
    },
    /** Label kind ('incoherence' | 'truthful') or null when the video is unlabeled. */
    incoherenceLabelType: {
      type: String,
      default: null,
    },
    /** True while the parent is waiting for the two timeline clicks that set the lie range. */
    incoherencePickActive: {
      type: Boolean,
      default: false,
    },
    /** Disables the lie buttons while the lie label is loading/saving. */
    incoherenceBusy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      blockModalOpen: false,
      modalBlock: null,
      modalAlertId: null,
      modalAlertIds: null,
      hoveredWordIndex: null,
      expandedMatchedTextByKey: {},
      /** Word user last jumped to; cleared once playhead matches that word (highlights click target). */
      lastSeekWordIndex: null,
      alertFieldsHelpOpen: false,
      /** Confirmation dialog before clearing the per-video lie label. */
      incoherenceClearConfirmOpen: false,
      /** Confirmation dialog before a truthful mark replaces an existing lie range. */
      incoherenceTruthfulConfirmOpen: false,
      lyricsEdgeSpacerPx: 120,
      lyricsLineHeightPx: 36,
      lastActiveVisualLineTopPx: null,
      lockedWordWidthsByIndex: {},
      relockWordWidthsRaf: null,
      /** Map of block_id → { is_incoherent: boolean } for persisted labels (VP cards) */
      labels: {},
      labelsSaving: {},
      /** Per-alert verdicts from detection_feedback (target_type='idea_alert'). */
      alertVerdicts: {},
      /** Map of fn_${startMs} → { is_incoherent: boolean } for FN labels (plain lines) */
      fnLabels: {},
      fnLabelsSaving: {},
      hoveredPlainLineKey: null,
      feedbackDialogOpen: false,
      feedbackIdeaId: null,
      feedbackAlerts: [],
      feedbackBlockForRetoggle: null,
    };
  },

  computed: {
    wordTimingLeadMs() {
      // Small lead to improve perceived sync when audio lags slightly behind transcript timing.
      return 300;
    },
    /** Numeric source id for the labels / feedback APIs (never the composite key). */
    sourceId() {
      const id = parseAnalysisKey(this.interviewId).sourceId;
      return id == null ? this.interviewId : id;
    },
    /** Run being viewed (`?run=`); labels are read and written strictly for it. */
    runId() {
      return parseAnalysisKey(this.interviewId).runId;
    },
    /** Lie footer mirrors the gesture panel footer: desktop, expanded, real source only. */
    showIncoherenceFooter() {
      return (
        this.incoherenceControlsEnabled &&
        !this.collapsed &&
        this.interviewId != null &&
        this.$vuetify.breakpoint.mdAndUp
      );
    },
    incoherenceIsTruthful() {
      return this.incoherenceLabelType === 'truthful';
    },
    incoherenceHasLabel() {
      return Boolean(this.incoherenceRangeMs) || this.incoherenceIsTruthful;
    },
    incoherenceButtonColor() {
      if (this.incoherenceIsTruthful) return 'success';
      return this.incoherenceRangeMs ? 'red lighten-1' : 'white';
    },
    incoherenceButtonTooltip() {
      if (this.incoherencePickActive) return 'Cancel — click the video timeline to set the lie range';
      if (this.incoherenceIsTruthful) return 'Video labeled as truthful';
      if (this.incoherenceRangeMs) {
        return `Lie ${this.formatMsClock(this.incoherenceRangeMs.startMs)} - ${this.formatMsClock(this.incoherenceRangeMs.endMs)}`;
      }
      return 'Label the lie in the video';
    },
    lyricsEdgeSpacerStyle() {
      return { height: `${this.lyricsEdgeSpacerPx}px` };
    },
    /** Max height for the lyrics scroller when `compactLyricsViewport` (four lines). */
    compactLyricsScrollStyle() {
      if (!this.compactLyricsViewport) return {};
      const lh = Math.max(22, Number(this.lyricsLineHeightPx) || 34);
      return { maxHeight: `${Math.round(lh * 4)}px` };
    },
    segmentTab: {
      get() {
        // Ideas are the only grouping (a gesture counts only in the idea it appears
        // in); stored 'merged' / 'per_alert' preferences read as 'idea'. 'none' is
        // the grouping-off state driven by the Group by switch.
        return normalizeSegmentationTab(this.segmentationTab);
      },
      set(v) {
        this.$emit('update:segmentationTab', v);
      },
    },
    groupByEnabled: {
      get() {
        return this.segmentTab !== 'none';
      },
      set(enabled) {
        if (!enabled) {
          this.segmentTab = 'none';
          this.scoreTab = 'computed_subtraction';
          return;
        }
        if (this.segmentTab === 'none') {
          this.segmentTab = 'idea';
          this.scoreTab = 'computed_subtraction';
        }
      },
    },
    scoreModeOptions() {
      return [
        { text: 'Alerts Subtraction', value: 'computed_subtraction' },
        { text: 'Alert Major', value: 'alert_major' },
      ];
    },
    scoreTab: {
      get() {
        return this.scoreMode;
      },
      set(v) {
        this.$emit('update:scoreMode', v);
      },
    },
    fontScaleTab: {
      get() {
        const scale = Number(this.fontScale);
        return Number.isFinite(scale) ? Math.max(12 / 24, Math.min(24 / 24, scale)) : 20 / 24;
      },
      set(v) {
        const scale = Number(v);
        this.$emit('update:fontScale', Number.isFinite(scale) ? scale : 20 / 24);
      },
    },
    fontSizePxTab: {
      get() {
        const px = Number(this.fontScaleTab) * 24;
        const snapped = Math.round(px / 2) * 2;
        return Math.max(12, Math.min(24, snapped));
      },
      set(v) {
        const px = Number(v);
        const snapped = Math.round(px / 2) * 2;
        const clamped = Math.max(12, Math.min(24, snapped));
        this.fontScaleTab = clamped / 24;
      },
    },
    lyricsPanelStyle() {
      return {
        '--semantics-font-scale': String(this.fontScaleTab),
      };
    },
    segmentStrategyLabel() {
      return this.segmentTab === 'none' ? 'None' : 'Idea';
    },
    semanticsMetaLabel() {
      if (this.segmentTab === 'none') return 'None';
      return `${this.segmentStrategyLabel} · ${this.scoreModeLabel}`;
    },
    scoreModeLabel() {
      return this.scoreTab === 'computed_subtraction' ? 'Alerts Subtraction' : 'Alert Major';
    },
    wordsDia() {
      const list = this.semanticModes?.diarization?.words;
      if (!Array.isArray(list)) return [];
      return list
        .slice()
        .sort((a, b) => {
          const ai = Number(a?.word_index);
          const bi = Number(b?.word_index);
          if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) return ai - bi;
          return Number(a?.start_time_ms) - Number(b?.start_time_ms);
        });
    },
    blocksForMode() {
      return semanticBlocksForSegmentStrategy(this.semanticModes?.diarization, this.segmentTab);
    },
    /**
     * Engine idea blocks carrying the new below-threshold candidate flags
     * (below_threshold_sum_reduction / below_threshold_alert_major). Used as a
     * fallback for idea-mode blocks rebuilt client-side, which lack the flags.
     */
    engineFlaggedIdeaBlocks() {
      const list = this.semanticModes?.diarization?.blocks_idea;
      if (!Array.isArray(list)) return [];
      return list.filter(
        (b) => b?.below_threshold_sum_reduction === true || b?.below_threshold_alert_major === true
      );
    },
    /**
     * Fallback below-threshold flags for displayed blocks that lack their own
     * flags (idea mode rebuilds blocks client-side and drops them). Each
     * flagged engine idea block is attributed to exactly ONE displayed block:
     * the block whose highlight_word_indexes contains the engine block's
     * anchor_word_index (client phrases partition the word stream, so this
     * yields a single card per engine candidate). Only when the anchor is
     * missing/invalid does it fall back to the single displayed block with
     * the maximum fractional time overlap — never to every overlapping block.
     * Keyed by displayed block_id.
     */
    fallbackBelowThresholdFlagsByBlockId() {
      const map = new Map();
      const engineBlocks = this.engineFlaggedIdeaBlocks;
      if (!engineBlocks.length) return map;
      const clientBlocks = this.blocksForMode.filter((b) => {
        if (!b || (b.block_id ?? b.blockId) == null) return false;
        const hasOwnFlags =
          b.below_threshold_sum_reduction !== undefined ||
          b.below_threshold_alert_major !== undefined;
        return !hasOwnFlags;
      });
      if (!clientBlocks.length) return map;

      const highlightSets = clientBlocks.map((b) => {
        const raw = b?.highlight_word_indexes ?? b?.highlightWordIndexes;
        const set = new Set();
        if (Array.isArray(raw)) {
          raw.forEach((x) => {
            const n = Number(x);
            if (Number.isFinite(n)) set.add(n);
          });
        }
        return set;
      });

      const markFlags = (clientBlock, engineBlock) => {
        const id = String(clientBlock.block_id ?? clientBlock.blockId);
        const entry = map.get(id) || { resta: false, alertMajor: false };
        if (engineBlock.below_threshold_sum_reduction === true) entry.resta = true;
        if (engineBlock.below_threshold_alert_major === true) entry.alertMajor = true;
        map.set(id, entry);
      };

      engineBlocks.forEach((eb) => {
        const anchor = Number(eb?.anchor_word_index ?? eb?.anchorWordIndex);
        if (Number.isFinite(anchor)) {
          const idx = highlightSets.findIndex((set) => set.has(anchor));
          if (idx !== -1) markFlags(clientBlocks[idx], eb);
          return;
        }
        // Anchor missing/invalid: single best match by fractional overlap of
        // the engine block's time range.
        const es = Number(eb?.start_time_ms ?? eb?.startTimeMs);
        const ee = Number(eb?.end_time_ms ?? eb?.endTimeMs);
        if (!Number.isFinite(es) || !Number.isFinite(ee) || ee <= es) return;
        let best = null;
        let bestFraction = 0;
        clientBlocks.forEach((cb) => {
          const cs = Number(cb?.start_time_ms ?? cb?.startTimeMs);
          const ce = Number(cb?.end_time_ms ?? cb?.endTimeMs);
          if (!Number.isFinite(cs) || !Number.isFinite(ce)) return;
          const overlap = Math.min(ee, ce) - Math.max(es, cs);
          if (overlap <= 0) return;
          const fraction = overlap / (ee - es);
          if (fraction > bestFraction) {
            bestFraction = fraction;
            best = cb;
          }
        });
        if (best) markFlags(best, eb);
      });
      return map;
    },
    /**
     * Stable order of diarization speaker labels (encounter order on the words
     * stream). Used to assign deterministic "Speaker N" fallbacks when no real
     * name is available for a label.
     */
    speakerLabelOrder() {
      const order = [];
      const seen = new Set();
      this.wordsDia.forEach((w) => {
        const label = String(w?.speaker ?? '').trim();
        if (!label || seen.has(label)) return;
        seen.add(label);
        order.push(label);
      });
      return order;
    },
    /**
     * speaker_label -> display string. Real inferred name when present and
     * confidence >= 0.6; otherwise "Speaker N" by encounter order; "unknown"
     * when the label itself is empty or marked unknown.
     */
    speakerDisplayByLabel() {
      const map = {};
      const namesRaw = this.semanticModes?.diarization?.speaker_names || {};
      const order = this.speakerLabelOrder;
      order.forEach((label, idx) => {
        const entry = namesRaw[label];
        const conf = Number(entry?.confidence);
        const name = String(entry?.name ?? '').trim();
        if (name && name.toLowerCase() !== 'unknown' && Number.isFinite(conf) && conf >= 0.6) {
          map[label] = name;
        } else {
          map[label] = `Speaker ${idx + 1}`;
        }
      });
      return map;
    },
    blocksPerAlert() {
      const list = this.semanticModes?.diarization?.blocks_per_alert;
      return Array.isArray(list) ? list : [];
    },
    perAlertBlockByAlertId() {
      const map = new Map();
      this.blocksPerAlert.forEach((block) => {
        const ids = this.contributingAlertIds(block);
        if (!ids.length) return;
        const alertId = String(ids[0]);
        if (!map.has(alertId)) map.set(alertId, block);
      });
      return map;
    },
    mergedParentBlocks() {
      const diarization = this.semanticModes?.diarization;
      const list = diarization?.blocks_merged_parents;
      if (Array.isArray(list) && list.length) return list;
      const merged = Array.isArray(diarization?.blocks_merged) ? diarization.blocks_merged : [];
      return merged.filter((b) => {
        const kind = String(b?.block_kind ?? b?.blockKind ?? '').trim();
        return kind === 'merged_parent';
      });
    },
    blockById() {
      const map = new Map();
      const all = [...this.blocksForMode, ...this.mergedParentBlocks];
      all.forEach((b) => {
        const id = b?.block_id ?? b?.blockId;
        if (id != null) map.set(String(id), b);
      });
      return map;
    },
    anchorBlocksByWordIndex() {
      const map = new Map();
      this.blocksForMode.forEach((b) => {
        const idx = Number(b?.anchor_word_index ?? b?.anchorWordIndex);
        if (!Number.isFinite(idx)) return;
        if (!map.has(idx)) map.set(idx, []);
        map.get(idx).push(b);
      });
      return map;
    },
    activeBlockWordIndexes() {
      const set = new Set();
      const b = this.activeBlock;
      if (!b) return set;
      const highlightRaw = b?.highlight_word_indexes ?? b?.highlightWordIndexes;
      if (Array.isArray(highlightRaw)) {
        highlightRaw.forEach((x) => {
          const n = Number(x);
          if (Number.isFinite(n)) set.add(n);
        });
      }
      const anchor = Number(b?.anchor_word_index ?? b?.anchorWordIndex);
      if (Number.isFinite(anchor)) set.add(anchor);
      return set;
    },
    activeBlock() {
      const ms = this.currentMs;
      const inRange = this.blocksForMode.filter((b) => {
        const s = Number(b?.start_time_ms);
        const e = Number(b?.end_time_ms);
        return Number.isFinite(s) && Number.isFinite(e) && ms >= s && ms <= e;
      });
      if (!inRange.length) return null;
      // Keep semaphore and word-level coherence aligned when blocks overlap.
      return this.selectMostSevereBlock(inRange);
    },
    /** All words marked in any semantic block for this mode (not only while playhead is inside). */
    allAlertHighlightWordIndexes() {
      const set = new Set();
      const blocks = this.blocksForMode;
      if (!Array.isArray(blocks)) return set;
      blocks.forEach((b) => {
        const raw = b?.highlight_word_indexes ?? b?.highlightWordIndexes;
        if (Array.isArray(raw)) {
          raw.forEach((x) => {
            const n = Number(x);
            if (Number.isFinite(n)) set.add(n);
          });
        }
      });
      return set;
    },
    allAlertAnchorWordIndexes() {
      const set = new Set();
      this.alertPillsByWordIndex.forEach((_v, key) => {
        const idx = Number(key);
        if (Number.isFinite(idx)) set.add(idx);
      });
      return set;
    },
    alertPillsByWordIndex() {
      const byWord = new Map();
      this.blocksPerAlert.forEach((block) => {
        const anchor = Number(block?.anchor_word_index ?? block?.anchorWordIndex);
        if (!Number.isFinite(anchor)) return;
        const ids = this.contributingAlertIds(block);
        const alertId = ids.length ? String(ids[0]) : null;
        const alert = alertId ? this.alertIndexById.get(alertId) : null;
        const subtractionRaw = alert ? this.alertSubtractionPercent(alert) : null;
        const subtraction =
          Number.isFinite(Number(subtractionRaw))
            ? Math.abs(Number(subtractionRaw))
            : Math.max(
              0,
              100 - Number(block?.score_resta_computada_after ?? block?.scoreRestaComputadaAfter)
            );
        const pill = {
          alertId,
          alert,
          block,
          subtraction: Number.isFinite(Number(subtraction)) ? Number(subtraction) : null,
        };
        if (!byWord.has(anchor)) byWord.set(anchor, []);
        byWord.get(anchor).push(pill);
      });
      byWord.forEach((list) => {
        list.sort((a, b) => {
          const at = Number(a?.alert?.gesture_timestamp_ms ?? a?.alert?.gestureTimestampMs);
          const bt = Number(b?.alert?.gesture_timestamp_ms ?? b?.alert?.gestureTimestampMs);
          if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at - bt;
          const aa = String(a?.alertId ?? '');
          const bb = String(b?.alertId ?? '');
          return aa.localeCompare(bb);
        });
      });
      return byWord;
    },
    hoveredAlertRangeWordIndexes() {
      const hovered = Number(this.hoveredWordIndex);
      if (!Number.isFinite(hovered)) return new Set();
      const blocks = this.blocksForMode;
      if (!Array.isArray(blocks) || !blocks.length) return new Set();

      const set = new Set();
      let matched = false;
      blocks.forEach((b) => {
        const highlightRaw = b?.highlight_word_indexes ?? b?.highlightWordIndexes;
        const highlight = Array.isArray(highlightRaw)
          ? highlightRaw.map((x) => Number(x)).filter((x) => Number.isFinite(x))
          : [];
        const anchor = Number(b?.anchor_word_index ?? b?.anchorWordIndex);
        const containsHovered = Number.isFinite(anchor) && anchor === hovered;
        if (!containsHovered) return;

        matched = true;
        highlight.forEach((x) => set.add(x));
        if (Number.isFinite(anchor)) set.add(anchor);
      });

      return matched ? set : new Set();
    },
    currentWordIndex() {
      const ms = this.currentMs + this.wordTimingLeadMs;
      const words = this.wordsDia;
      if (!Array.isArray(words) || !words.length) return null;

      // wordsDia is already ordered by start_time_ms / word_index.
      // Use binary search to avoid filtering/sorting on every playhead tick.
      let lo = 0;
      let hi = words.length - 1;
      let best = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const s = Number(words[mid]?.start_time_ms);
        if (!Number.isFinite(s)) {
          hi = mid - 1;
          continue;
        }
        if (s <= ms) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      if (best < 0) return null;

      for (let i = best; i >= 0; i -= 1) {
        const w = words[i];
        const s = Number(w?.start_time_ms);
        const e = Number(w?.end_time_ms);
        if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
        if (ms >= s && ms <= e) {
          const idx = Number(w?.word_index);
          return Number.isFinite(idx) ? idx : null;
        }
        if (e < ms) break;
      }

      return null;
    },

    /** Transcript lines for lyrics rendering. */
    lyricsLines() {
      const words = this.wordsDia;
      if (!words.length) return [];
      if (this.segmentTab === 'none') {
        const startMs = Number(words[0]?.start_time_ms);
        const endMs = Number(words[words.length - 1]?.end_time_ms);
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return [];
        return [
          {
            lineKey: `plain-all-${startMs}-${endMs}`,
            words: words.slice(),
            start_ms: startMs,
            end_ms: endMs,
            speaker: 'mixed',
            alertBlock: null,
            segmentKey: null,
          },
        ];
      }
      const lines = [];
      const coveredWordIndexes = new Set();
      const wordsByIndex = new Map();
      words.forEach((w) => {
        const idx = Number(w?.word_index);
        if (Number.isFinite(idx)) wordsByIndex.set(idx, w);
      });

      const blocks = Array.isArray(this.blocksForMode) ? this.blocksForMode.slice() : [];
      blocks.sort((a, b) => {
        const as = Number(a?.start_time_ms);
        const bs = Number(b?.start_time_ms);
        if (Number.isFinite(as) && Number.isFinite(bs) && as !== bs) return as - bs;
        const ae = Number(a?.end_time_ms);
        const be = Number(b?.end_time_ms);
        if (Number.isFinite(ae) && Number.isFinite(be) && ae !== be) return ae - be;
        return String(a?.block_id ?? a?.blockId ?? '').localeCompare(
          String(b?.block_id ?? b?.blockId ?? '')
        );
      });

      blocks.forEach((block, blockIndex) => {
        const segKey = this.blockKey(block);
        if (!segKey) return;
        const raw = block?.highlight_word_indexes ?? block?.highlightWordIndexes;
        const indexes = Array.isArray(raw)
          ? raw.map((x) => Number(x)).filter((x) => Number.isFinite(x))
          : [];
        const uniqueIndexes = Array.from(new Set(indexes)).sort((a, b) => a - b);
        const blockWords = uniqueIndexes
          .map((idx) => wordsByIndex.get(idx))
          .filter(Boolean);
        if (!blockWords.length) return;

        uniqueIndexes.forEach((idx) => coveredWordIndexes.add(idx));
        const startMs = Number(block?.start_time_ms);
        const endMs = Number(block?.end_time_ms);
        lines.push({
          lineKey: `seg-${segKey}-${blockIndex}`,
          words: blockWords,
          start_ms: Number.isFinite(startMs) ? startMs : Number(blockWords[0]?.start_time_ms),
          end_ms: Number.isFinite(endMs) ? endMs : Number(blockWords[blockWords.length - 1]?.end_time_ms),
          speaker: 'mixed',
          alertBlock: block,
          segmentKey: segKey,
          _orderWordIndex: uniqueIndexes[0],
          _orderMs: Number.isFinite(startMs) ? startMs : Number(blockWords[0]?.start_time_ms),
          _isSegment: true,
        });
      });

      let plainRun = [];
      const flushPlain = () => {
        if (!plainRun.length) return;
        const startMs = Number(plainRun[0]?.start_time_ms);
        const endMs = Number(plainRun[plainRun.length - 1]?.end_time_ms);
        lines.push({
          lineKey: `plain-${plainRun[0].word_index}-${plainRun[plainRun.length - 1].word_index}`,
          words: plainRun.slice(),
          start_ms: startMs,
          end_ms: endMs,
          speaker: 'mixed',
          alertBlock: null,
          segmentKey: null,
          _orderWordIndex: Number(plainRun[0]?.word_index),
          _orderMs: startMs,
          _isSegment: false,
        });
        plainRun = [];
      };

      words.forEach((word) => {
        const idx = Number(word?.word_index);
        if (!Number.isFinite(idx) || coveredWordIndexes.has(idx)) {
          flushPlain();
          return;
        }
        plainRun.push(word);
      });
      flushPlain();

      lines.sort((a, b) => {
        const am = Number(a?._orderMs);
        const bm = Number(b?._orderMs);
        if (Number.isFinite(am) && Number.isFinite(bm) && am !== bm) return am - bm;
        const ai = Number(a?._orderWordIndex);
        const bi = Number(b?._orderWordIndex);
        if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) return ai - bi;
        if (Boolean(a?._isSegment) !== Boolean(b?._isSegment)) return a?._isSegment ? -1 : 1;
        return String(a?.lineKey ?? '').localeCompare(String(b?.lineKey ?? ''));
      });

      return lines.map((line) => ({
        lineKey: line.lineKey,
        words: line.words,
        start_ms: line.start_ms,
        end_ms: line.end_ms,
        speaker: line.speaker,
        alertBlock: line.alertBlock,
        segmentKey: line.segmentKey,
      }));
    },

    activeLyricsLineIndex() {
      const lines = this.lyricsLines;
      if (!lines.length) return -1;
      const ms = this.currentMs;
      for (let i = 0; i < lines.length; i++) {
        const s = lines[i].start_ms;
        const e = lines[i].end_ms;
        if (ms >= s && ms <= e) return i;
      }
      const next = lines.findIndex((l) => ms < l.start_ms);
      if (next === 0) return 0;
      if (next === -1) return lines.length - 1;
      return Math.max(0, next - 1);
    },
    alertIndexById() {
      const map = new Map();
      const list = Array.isArray(this.coherenceAlerts) ? this.coherenceAlerts : [];
      list.forEach((a) => {
        const id = a.alert_id ?? a.alertId;
        if (id != null && !map.has(String(id))) map.set(String(id), a);
      });
      return map;
    },
    /**
     * Alerts raised outside every idea (reason_category no_semantics): no idea
     * takes them and they have no word to hang a pill on, so they are listed
     * apart, in time order.
     */
    noSemanticsAlerts() {
      const list = Array.isArray(this.coherenceAlerts) ? this.coherenceAlerts : [];
      return list
        .filter((a) => this.alertReasonCategory(a) === 'no_semantics')
        .slice()
        .sort((a, b) => this.alertAnchorSeconds(a) - this.alertAnchorSeconds(b));
    },
    modalResolvedAlerts() {
      if (!this.modalBlock) return [];
      const ids = this.contributingAlertIds(this.modalBlock);
      const out = [];
      ids.forEach((id) => {
        const a = this.alertIndexById.get(String(id));
        if (a) out.push(a);
      });
      const sorted = out.slice().sort((a, b) => this.alertAnchorSeconds(a) - this.alertAnchorSeconds(b));
      if (Array.isArray(this.modalAlertIds) && this.modalAlertIds.length) {
        const allowed = new Set(this.modalAlertIds.map((id) => String(id)));
        return sorted.filter((a) => allowed.has(String(a?.alert_id ?? a?.alertId)));
      }
      if (!this.modalAlertId) return sorted;
      return sorted.filter((a) => String(a?.alert_id ?? a?.alertId) === String(this.modalAlertId));
    },

    /** Static copy for the alert field help dialog. */
    alertFieldHelpEntries() {
      return [
        {
          title: 'Gesture',
          body:
            'Identifier of the gesture or behavioral signal that triggered this coherence check. It links the alert to a specific movement pattern.',
        },
        {
          title: 'Time window',
          body:
            'Half-width in seconds around the gesture time (stored as metadata.window_seconds). The classifier reads the words of the idea the gesture appears in that fall within this window; the window never extends beyond that idea. A gesture in a pause between ideas has no idea to read and is raised without semantics.',
        },
        {
          title: 'Rule value',
          body:
            'The phrase, token, or pattern from the ruleset (metadata.source_value) that the engine compared against the text in that time window.',
        },
        {
          title: 'Matched speaker',
          body:
            'Speaker label from diarization for the transcript segment that was aligned with the gesture window (semantic_match.speaker).',
        },
        {
          title: 'Matched text',
          body:
            'The transcript snippet that was matched or scored in that window (semantic_match.text). Use it to see exactly what was evaluated.',
        },
      ];
    },

    currentMs() {
      return Math.max(0, Math.round(Number(this.currentVideoTime) * 1000));
    },
    displayCoherence() {
      const block = this.activeBlock;
      if (!block) return 100;
      const score = Number(this.blockScore(block));
      if (Number.isFinite(score)) return Math.round(score);
      return Math.round(this._meanCoherenceFromContributingIds(block));
    },
    showCoherenceIndicator() {
      // Show semaphore marker for any active semantic block, including 100% blocks.
      return !!this.activeBlock;
    },
    indicatorPillClass() {
      const v = this.displayCoherence;
      const g = Number(this.semaphoreThresholdGood);
      const w = Number(this.semaphoreThresholdWarn);
      const good = Number.isFinite(g) ? g : 80;
      const warn = Number.isFinite(w) ? w : 50;
      if (v >= good) return 'pill-good';
      if (v >= warn) return 'pill-warn';
      return 'pill-bad';
    },
    indicatorLeftPercent() {
      const mirrored = 100 - Number(this.displayCoherence);
      return Math.min(92, Math.max(8, mirrored));
    },
  },

  watch: {
    currentWordIndex(idx) {
      if (this.lastSeekWordIndex != null && idx != null && idx === this.lastSeekWordIndex) {
        this.lastSeekWordIndex = null;
      }
      this.followActiveVisualLine();
    },
    activeLyricsLineIndex(next, prev) {
      if (next < 0 || next === prev) return;
      this.emitActiveLyricsLineEvent(next);
    },
    lyricsLines(lines) {
      if (!Array.isArray(lines) || !lines.length) return;
      this.$nextTick(() => {
        this.lastActiveVisualLineTopPx = null;
        this.refreshLyricsViewportMetrics();
        this.scheduleRelockWordWidths();
        this.followActiveVisualLine({ behavior: 'auto', force: true });
      });
    },
    compactLyricsViewport() {
      this.lastActiveVisualLineTopPx = null;
      this.$nextTick(() => {
        this.refreshLyricsViewportMetrics();
        this.scheduleRelockWordWidths();
        this.followActiveVisualLine({ behavior: 'auto', force: true });
      });
    },
    fontScale() {
      // Font size changes alter token widths/line wraps; clear cached widths and relock
      // after layout settles so spacing remains consistent inside the lyrics container.
      this.lockedWordWidthsByIndex = {};
      this.lastActiveVisualLineTopPx = null;
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.refreshLyricsViewportMetrics();
          this.scheduleRelockWordWidths();
          this.followActiveVisualLine({ behavior: 'auto', force: true });
        });
      });
    },
    interviewId(id) {
      if (id) {
        this.loadLabels();
        this.loadAlertVerdicts();
      }
    },
    scoreTab() {
      this.loadLabels();
    },
  },

  mounted() {
    window.addEventListener('resize', this.onLyricsViewportResize, { passive: true });

    this.$nextTick(() => {
      this.refreshLyricsViewportMetrics();
      this.scheduleRelockWordWidths();
      this.followActiveVisualLine({ behavior: 'auto', force: true });
      if (typeof ResizeObserver !== 'undefined' && this.$el) {
        this._lyricsResizeObserver = new ResizeObserver(this.onLyricsViewportResize);
        this._lyricsResizeObserver.observe(this.$el);
      }
    });
    if (this.interviewId) {
      this.loadLabels();
      this.loadAlertVerdicts();
    }
    if (document && document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(() => {
        this.scheduleRelockWordWidths();
      });
    }
  },

  beforeDestroy() {
    if (this._seekTargetClearTimer) window.clearTimeout(this._seekTargetClearTimer);
    window.removeEventListener('resize', this.onLyricsViewportResize);
    if (this._lyricsResizeObserver) this._lyricsResizeObserver.disconnect();
    if (this.relockWordWidthsRaf) {
      window.cancelAnimationFrame(this.relockWordWidthsRaf);
      this.relockWordWidthsRaf = null;
    }
  },

  methods: {
    ...mapActions('analysisData', ['requestInterviewVideoSeek']),

    isActiveBlock(block) {
      if (!block || !this.activeBlock) return false;
      const a = this.activeBlock;
      const aId = a.block_id ?? a.blockId ?? null;
      const bId = block.block_id ?? block.blockId ?? null;
      if (aId != null && bId != null) return aId === bId;
      return a === block;
    },

    blockSpeakerDisplay(block) {
      const label = String(block?.speaker ?? '').trim();
      if (!label || label.toLowerCase() === 'unknown') return 'unknown';
      // "mixed" is emitted when an idea spans multiple speakers (see
      // semantics builder); show it verbatim so the analyst sees the case.
      if (label.toLowerCase() === 'mixed') return 'Mixed';
      return this.speakerDisplayByLabel[label] || label;
    },

    /** User confirmed the clear-incoherence dialog: close it and ask the parent to remove the label. */
    confirmIncoherenceClear() {
      this.incoherenceClearConfirmOpen = false;
      this.$emit('incoherence-clear');
    },

    /** Marking truthful replaces an existing lie range, so that case asks first. */
    onMarkTruthfulClick() {
      if (this.incoherenceRangeMs) {
        this.incoherenceTruthfulConfirmOpen = true;
        return;
      }
      this.$emit('incoherence-mark-truthful');
    },

    confirmIncoherenceTruthful() {
      this.incoherenceTruthfulConfirmOpen = false;
      this.$emit('incoherence-mark-truthful');
    },

    async loadLabels() {
      if (!this.interviewId) return;
      const sourceType = this.isManualAnalysis ? 'manual_analysis' : 'interview';
      try {
        const { data } = await CoherenceLabelService.getLabels(
          sourceType,
          this.sourceId,
          this.scoreTab,
          this.runId
        );
        const map = {};
        const fnMap = {};
        for (const label of data.data || []) {
          if (label.block_id) {
            map[label.block_id] = label;
          } else if (label.segment_start_ms != null) {
            fnMap[`fn_${label.segment_start_ms}`] = label;
          }
        }
        this.labels = map;
        this.fnLabels = fnMap;
      } catch (e) {
        // silent — labels are non-critical
      }
    },

    /**
     * Hydrate per-alert verdicts (target_type='idea_alert') so the modal can
     * surface a Correct/Incorrect chip next to each alert the analyst has
     * already reviewed.
     */
    async loadAlertVerdicts() {
      if (!this.interviewId) return;
      const sourceType = this.isManualAnalysis ? 'manual_analysis' : 'interview';
      try {
        const { data } = await DetectionFeedbackService.list(sourceType, this.sourceId, this.runId);
        const items = Array.isArray(data?.data?.items) ? data.data.items : [];
        const next = {};
        for (const it of items) {
          if (it.target_type !== 'idea_alert') continue;
          if (!it.target_ref) continue;
          if (it.verdict === 'correct' || it.verdict === 'incorrect') {
            next[String(it.target_ref)] = it.verdict;
          }
        }
        this.alertVerdicts = next;
      } catch (e) {
        // silent
      }
    },

    blockLabel(block) {
      const id = block?.block_id ?? block?.blockId;
      return id ? this.labels[id] ?? null : null;
    },

    fnLabelKey(line) {
      return line?.start_ms != null ? `fn_${line.start_ms}` : null;
    },

    async toggleFNLabel(line) {
      const key = this.fnLabelKey(line);
      if (!key || !this.interviewId) return;

      const existing = this.fnLabels[key];
      const newValue = existing?.is_incoherent === true ? null : true;

      this.$set(this.fnLabelsSaving, key, true);
      try {
        const sourceType = this.isManualAnalysis ? 'manual_analysis' : 'interview';
        await CoherenceLabelService.upsertLabel({
          source_type: sourceType,
          source_id: this.sourceId,
          ...(this.runId ? { run_id: this.runId } : {}),
          score_mode: this.scoreTab,
          block_id: null,
          segment_start_ms: line.start_ms ?? null,
          segment_end_ms: line.end_ms ?? null,
          is_incoherent: newValue,
        });
        if (newValue === null) {
          this.$delete(this.fnLabels, key);
        } else {
          this.$set(this.fnLabels, key, { is_incoherent: true, segment_start_ms: line.start_ms });
        }
      } catch (e) {
        // silent
      } finally {
        this.$set(this.fnLabelsSaving, key, false);
      }
    },

    async toggleLabel(block, isIncoherent) {
      const id = block?.block_id ?? block?.blockId;
      if (!id || !this.interviewId) return;

      const existing = this.labels[id];
      const newValue = existing?.is_incoherent === isIncoherent ? null : isIncoherent;

      // When the user is *opening* the "incorrect" state (newValue === false), surface
      // the granular feedback dialog so they can flag which specific alerts are wrong
      // and add missing alerts. The persisted block-level label still flips below.
      if (newValue === false) {
        const ids = this.contributingAlertIds(block);
        // Resolve human_label from the ruleset (it's per-rule, not per-alert).
        const snap = this.$store?.state?.analysisData?.gestures?.[this.interviewId]?.analysis_config_snapshot
          || this.$store?.state?.analysisData?.gestures?.[this.interviewId]?.analysisConfigSnapshot
          || null;
        const rules = snap?.engines?.coherence?.ruleset?.rules || [];
        const humanLabelByRuleId = {};
        for (const r of (Array.isArray(rules) ? rules : [])) {
          const rid = r?.ruleId ?? r?.rule_id;
          if (rid) humanLabelByRuleId[String(rid)] = r?.humanLabel ?? r?.human_label ?? null;
        }
        const alerts = ids
          .map((aid) => this.alertIndexById.get(String(aid)))
          .filter(Boolean)
          .map((a) => {
            const ruleId = a.rule_id ?? a.ruleId ?? null;
            return {
              id: String(a.alert_id ?? a.alertId),
              rule_id: ruleId,
              rule_label: a.rule_label ?? a.ruleLabel,
              human_label: humanLabelByRuleId[String(ruleId)] || a.human_label || a.humanLabel || null,
              // Persisted on the feedback row: always the current catalogue id.
              gesture_name: currentGestureName(a.gesture_name ?? a.gestureName ?? null),
              timestamp_ms: a.gesture_timestamp_ms ?? a.gestureTimestampMs,
              // Durable coordinate for the feedback row (alert_id changes every run).
              start_ms: (a.gesture_time_range_ms ?? a.gestureTimeRangeMs)?.[0] ?? null,
              end_ms: (a.gesture_time_range_ms ?? a.gestureTimeRangeMs)?.[1] ?? null,
              subtraction: this.alertSubtractionPercent ? this.alertSubtractionPercent(a) : null,
              confidence: a.confidence ?? null,
              explain: a.explain ?? null,
            };
          });
        this.feedbackIdeaId = String(id);
        this.feedbackAlerts = alerts;
        this.feedbackBlockForRetoggle = block;
        this.feedbackDialogOpen = true;
      }

      this.$set(this.labelsSaving, id, true);
      try {
        const sourceType = this.isManualAnalysis ? 'manual_analysis' : 'interview';
        await CoherenceLabelService.upsertLabel({
          source_type: sourceType,
          source_id: this.sourceId,
          ...(this.runId ? { run_id: this.runId } : {}),
          score_mode: this.scoreTab,
          block_id: id,
          segment_start_ms: block?.start_time_ms ?? null,
          segment_end_ms: block?.end_time_ms ?? null,
          is_incoherent: newValue,
        });
        if (newValue === null) {
          this.$delete(this.labels, id);
        } else {
          this.$set(this.labels, id, { block_id: id, is_incoherent: newValue });
        }
      } catch (e) {
        // silent
      } finally {
        this.$set(this.labelsSaving, id, false);
      }
    },

    onFeedbackSaved(payload) {
      // Patch local verdicts immediately for snappy UI.
      const verdicts = payload && payload.verdicts ? payload.verdicts : null;
      if (verdicts) {
        for (const [key, value] of Object.entries(verdicts)) {
          if (value === 'correct' || value === 'incorrect') {
            this.$set(this.alertVerdicts, String(key), value);
          }
        }
      }
      // Re-sync from backend.
      this.loadAlertVerdicts();
      this.$emit('feedback-saved');
    },

    onLyricsViewportResize() {
      this.lastActiveVisualLineTopPx = null;
      this.lockedWordWidthsByIndex = {};
      this.refreshLyricsViewportMetrics();
      this.scheduleRelockWordWidths();
      this.followActiveVisualLine({ behavior: 'auto', force: true });
    },

    refreshLyricsViewportMetrics() {
      const scrollEl = this.$refs.segmentsScroll;
      if (!scrollEl) return;
      const next = Math.round(scrollEl.clientHeight * 0.42);
      this.lyricsEdgeSpacerPx = Math.max(72, Number.isFinite(next) ? next : 72);
      const firstLine = scrollEl.querySelector('.lyrics-line');
      if (firstLine) {
        const styles = window.getComputedStyle(firstLine);
        const lineHeightPx = Number.parseFloat(styles.lineHeight);
        if (Number.isFinite(lineHeightPx) && lineHeightPx > 0) {
          this.lyricsLineHeightPx = lineHeightPx;
        } else {
          const fontSizePx = Number.parseFloat(styles.fontSize);
          if (Number.isFinite(fontSizePx) && fontSizePx > 0) {
            this.lyricsLineHeightPx = fontSizePx * 1.45;
          }
        }
      }
    },

    withNextFrame(fn) {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          fn();
        });
      });
    },

    wordTokenInlineStyle(wordIndex) {
      const key = String(wordIndex);
      const widthPx = Number(this.lockedWordWidthsByIndex[key]);
      if (!Number.isFinite(widthPx) || widthPx <= 0) return null;
      return {
        '--lyrics-token-lock-width': `${Math.round(widthPx)}px`,
      };
    },

    mergedWordTokenStyle(wordIndex) {
      const base = this.wordTokenInlineStyle(wordIndex) || {};
      const merged = { ...base };
      return Object.keys(merged).length ? merged : null;
    },

    scheduleRelockWordWidths() {
      if (this.relockWordWidthsRaf) {
        window.cancelAnimationFrame(this.relockWordWidthsRaf);
        this.relockWordWidthsRaf = null;
      }
      this.relockWordWidthsRaf = window.requestAnimationFrame(() => {
        this.relockWordWidthsRaf = null;
        this.lockWordWidthsForStableLayout();
      });
    },

    lockWordWidthsForStableLayout() {
      const root = this.$el;
      if (!root) return;
      const wordEls = root.querySelectorAll('.words-live-token--lyrics[data-word-index]');
      if (!wordEls || !wordEls.length) return;

      const next = {};
      wordEls.forEach((el) => {
        const key = String(el.getAttribute('data-word-index') || '').trim();
        if (!key) return;
        const measured = Math.ceil(el.offsetWidth) + 2;
        if (!Number.isFinite(measured) || measured <= 0) return;
        const prev = Number(next[key] || 0);
        next[key] = measured > prev ? measured : prev;
      });

      if (!Object.keys(next).length) return;
      this.lockedWordWidthsByIndex = next;
    },

    followActiveVisualLine({ behavior = 'smooth', force = false } = {}) {
      this.withNextFrame(() => {
        const scrollEl = this.$refs.segmentsScroll;
        if (!scrollEl) return;
        const currentWordEl = scrollEl.querySelector('.words-live-token--lyrics.words-live-token--current');
        if (!currentWordEl) return;

        const scrollRect = scrollEl.getBoundingClientRect();
        const wordRect = currentWordEl.getBoundingClientRect();
        const visualLineTopPx = scrollEl.scrollTop + (wordRect.top - scrollRect.top);

        const prevTop = Number(this.lastActiveVisualLineTopPx);
        const changedVisualLine =
          !Number.isFinite(prevTop) || Math.abs(visualLineTopPx - prevTop) >= 2;
        if (!force && !changedVisualLine) return;

        this.lastActiveVisualLineTopPx = visualLineTopPx;
        this.$emit('lyrics-active-visual-line-change', {
          wordIndex: this.currentWordIndex,
          visualLineTopPx,
        });

        const lineHeightPx = Math.max(20, Number(this.lyricsLineHeightPx) || 36);
        const viewH = scrollEl.clientHeight;
        // Tall desktop viewport: keep several lines of context above the active line.
        // Compact (two-line) viewport: center the active line in the visible strip.
        const focusY =
          this.compactLyricsViewport && viewH > 0
            ? viewH / 2
            : lineHeightPx * 4.5;
        const desiredTop = visualLineTopPx + wordRect.height / 2 - focusY;
        const maxTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
        const nextTop = Math.max(0, Math.min(maxTop, desiredTop));
        if (!force && Math.abs(scrollEl.scrollTop - nextTop) < 1) return;

        const scrollBehavior = this.compactLyricsViewport ? 'auto' : behavior;
        if (scrollBehavior === 'smooth' && typeof scrollEl.scrollTo === 'function') {
          scrollEl.scrollTo({ top: nextTop, behavior: 'smooth' });
          return;
        }
        scrollEl.scrollTop = nextTop;
      });
    },

    emitActiveLyricsLineEvent(targetIndex) {
      const lines = this.lyricsLines;
      const line = Array.isArray(lines) ? lines[targetIndex] : null;
      this.$emit('lyrics-active-line-change', {
        lineIndex: targetIndex,
        lineKey: line?.lineKey || null,
        startSeconds: Number.isFinite(Number(line?.start_ms)) ? Number(line.start_ms) / 1000 : null,
        endSeconds: Number.isFinite(Number(line?.end_ms)) ? Number(line.end_ms) / 1000 : null,
      });
    },

    /**
     * Single source of truth for word-level severity across overlaps:
     * use the same score shown in word alert pills (blockScore in active score mode).
     */
    effectiveBlockWordScore(block) {
      if (!block) return Number.POSITIVE_INFINITY;
      const score = Number(this.blockScore(block));
      if (Number.isFinite(score)) return score;

      // Legacy fallback if score fields are missing in a payload.
      const ids = this.contributingAlertIds(block);
      if (ids.length) {
        let worst = Number.POSITIVE_INFINITY;
        ids.forEach((id) => {
          const a = this.alertIndexById.get(String(id));
          const t = Number(a?.coherence_after ?? a?.coherenceAfter);
          if (Number.isFinite(t)) worst = Math.min(worst, t);
        });
        if (Number.isFinite(worst)) return worst;
      }
      return Number.POSITIVE_INFINITY;
    },

    selectMostSevereBlock(blocks) {
      if (!Array.isArray(blocks) || !blocks.length) return null;
      return blocks.reduce((worst, block) => {
        if (!worst) return block;
        return this.effectiveBlockWordScore(block) < this.effectiveBlockWordScore(worst)
          ? block
          : worst;
      }, null);
    },

    getPrimaryAnchorBlockForWordIndex(wordIndex) {
      const idx = Number(wordIndex);
      if (!Number.isFinite(idx)) return null;
      const list = this.anchorBlocksByWordIndex.get(idx);
      return this.selectMostSevereBlock(list);
    },

    resolveModalBlockForAnchor(anchorBlock) {
      if (!anchorBlock) return null;
      if (this.segmentTab !== 'merged') return anchorBlock;
      const parentId = anchorBlock?.parent_block_id ?? anchorBlock?.parentBlockId;
      if (!parentId) return anchorBlock;
      return this.blockById.get(String(parentId)) || anchorBlock;
    },

    /**
     * Word-level badges/tone must use the anchor child block score.
     * Do not resolve to merged parent here, otherwise values diverge
     * from the segment color when parent and child scores differ.
     */
    anchorScoreDisplayBlock(wordIndex) {
      const anchorBlock = this.getPrimaryAnchorBlockForWordIndex(Number(wordIndex));
      if (!anchorBlock) return null;
      return anchorBlock;
    },

    onWordHoverStart(wordIndex) {
      const anchorBlock = this.getPrimaryAnchorBlockForWordIndex(wordIndex);
      const idx = Number(anchorBlock?.anchor_word_index ?? anchorBlock?.anchorWordIndex);
      this.hoveredWordIndex = Number.isFinite(idx) ? idx : null;
    },

    onWordHoverEnd() {
      this.hoveredWordIndex = null;
    },

    blockKey(b) {
      if (!b) return null;
      const id = b.block_id ?? b.blockId;
      const s = Number(b.start_time_ms);
      const e = Number(b.end_time_ms);
      // Use a composite key when possible to avoid collisions between overlapping
      // blocks that may share the same block_id but have different ranges/scores.
      if (id != null && String(id).trim() !== '' && Number.isFinite(s) && Number.isFinite(e)) {
        return `${String(id)}@${s}-${e}`;
      }
      if (id != null && String(id).trim() !== '') return String(id);
      if (Number.isFinite(s) && Number.isFinite(e)) return `t:${s}-${e}`;
      return null;
    },

    /**
     * Stable id for grouping consecutive words that belong to the same semantic block
     * (anchor or highlight list). Used for a single shared outline per segment.
     */
    segmentBlockIdForWord(wordIndex) {
      const idx = Number(wordIndex);
      if (!Number.isFinite(idx)) return null;
      const blocks = this.blocksForMode;
      if (!Array.isArray(blocks) || !blocks.length) return null;
      const matchingBlocks = [];
      for (let i = 0; i < blocks.length; i += 1) {
        const b = blocks[i];
        const anchor = Number(b?.anchor_word_index ?? b?.anchorWordIndex);
        if (anchor === idx) matchingBlocks.push(b);
      }
      if (!matchingBlocks.length) {
        for (let i = 0; i < blocks.length; i += 1) {
          const b = blocks[i];
          const raw = b?.highlight_word_indexes ?? b?.highlightWordIndexes;
          const highlights = Array.isArray(raw)
            ? raw.map((x) => Number(x)).filter((x) => Number.isFinite(x))
            : [];
          if (highlights.includes(idx)) matchingBlocks.push(b);
        }
      }
      const block = this.selectMostSevereBlock(matchingBlocks);
      return block ? this.blockKey(block) : null;
    },

    lyricsLineRuns(line) {
      const words = line?.words;
      if (!Array.isArray(words) || !words.length) return [];
      if (this.segmentTab === 'none') {
        return [{ segId: null, words: words.slice() }];
      }
      const out = [];
      let cur = { segId: null, words: [] };
      const flush = () => {
        if (cur.words.length) out.push({ segId: cur.segId, words: cur.words.slice() });
        cur = { segId: null, words: [] };
      };
      for (let i = 0; i < words.length; i += 1) {
        const word = words[i];
        const idx = Number(word.word_index);
        const segId = this.segmentBlockIdForWord(idx);
        if (cur.words.length === 0) {
          cur.segId = segId;
          cur.words.push(word);
        } else if (segId === cur.segId) {
          cur.words.push(word);
        } else {
          flush();
          cur.segId = segId;
          cur.words.push(word);
        }
      }
      flush();
      return out;
    },

    blockBySegmentKey(segId) {
      if (segId == null || segId === '') return null;
      const blocks = this.blocksForMode;
      if (!Array.isArray(blocks)) return null;
      for (let i = 0; i < blocks.length; i += 1) {
        const b = blocks[i];
        if (this.blockKey(b) === segId) return b;
      }
      return null;
    },

    /**
     * Same segment continues from the previous lyrics line (open left edge).
     */
    segmentRunContinuesFromPreviousLine(lineIdx, run) {
      if (!run?.segId || !run.words?.length) return false;
      const line = this.lyricsLines[lineIdx];
      if (!line?.words?.length) return false;
      const firstIdx = Number(run.words[0].word_index);
      const pos = line.words.findIndex((w) => Number(w.word_index) === firstIdx);
      if (pos !== 0) return false;
      if (lineIdx <= 0) return false;
      const prevLine = this.lyricsLines[lineIdx - 1];
      if (!prevLine?.words?.length) return false;
      const prevW = prevLine.words[prevLine.words.length - 1];
      return this.segmentBlockIdForWord(prevW.word_index) === run.segId;
    },

    /**
     * Same segment continues on the next lyrics line (open right edge).
     */
    segmentRunContinuesOnNextLine(lineIdx, run) {
      if (!run?.segId || !run.words?.length) return false;
      const line = this.lyricsLines[lineIdx];
      if (!line?.words?.length) return false;
      const lastIdx = Number(run.words[run.words.length - 1].word_index);
      const pos = line.words.findIndex((w) => Number(w.word_index) === lastIdx);
      if (pos !== line.words.length - 1) return false;
      if (lineIdx >= this.lyricsLines.length - 1) return false;
      const nextLine = this.lyricsLines[lineIdx + 1];
      if (!nextLine?.words?.length) return false;
      const nextW = nextLine.words[0];
      return this.segmentBlockIdForWord(nextW.word_index) === run.segId;
    },

    segmentRunWrapperClass(run, lineIdx) {
      if (!run?.segId) return 'lyrics-segment-run--plain';
      const parts = ['lyrics-segment-run--outlined'];
      const block = this.blockBySegmentKey(run.segId);
      const tone = block ? this.alertLineTone(block) : 'neutral';
      parts.push(`lyrics-segment-run--tone-${tone}`);
      if (this.segmentRunContinuesFromPreviousLine(lineIdx, run)) {
        parts.push('lyrics-segment-run--bridge-left');
      }
      if (this.segmentRunContinuesOnNextLine(lineIdx, run)) {
        parts.push('lyrics-segment-run--bridge-right');
      }
      return parts.join(' ');
    },

    isAnchorWord(wordIndex) {
      const idx = Number(wordIndex);
      return Number.isFinite(idx) && this.allAlertAnchorWordIndexes.has(idx);
    },

    alertPillsForWord(wordIndex, preferredBlock = null, line = null) {
      const idx = Number(wordIndex);
      if (!Number.isFinite(idx)) return [];
      const list = this.alertPillsByWordIndex.get(idx);
      if (!preferredBlock || !line) return Array.isArray(list) ? list : [];
      const allowed = new Set(this.contributingAlertIds(preferredBlock).map((id) => String(id)));
      if (!allowed.size) return [];
      const lineWords = Array.isArray(line?.words) ? line.words : [];
      if (!lineWords.length) return [];
      const lineWordIndexes = lineWords
        .map((w) => Number(w?.word_index))
        .filter((n) => Number.isFinite(n));
      const lineWordSet = new Set(lineWordIndexes);

      const pills = [];
      allowed.forEach((alertId) => {
        const alert = this.alertIndexById.get(String(alertId));
        const perAlertBlock = this.perAlertBlockByAlertId.get(String(alertId));
        const anchor = Number(perAlertBlock?.anchor_word_index ?? perAlertBlock?.anchorWordIndex);
        let displayWordIndex = Number.isFinite(anchor) && lineWordSet.has(anchor) ? anchor : null;

        if (!Number.isFinite(displayWordIndex)) {
          const targetMs = Number(alert?.gesture_timestamp_ms ?? alert?.gestureTimestampMs);
          if (Number.isFinite(targetMs)) {
            let bestIdx = null;
            let bestDist = Number.POSITIVE_INFINITY;
            lineWords.forEach((w) => {
              const wi = Number(w?.word_index);
              const ws = Number(w?.start_time_ms);
              if (!Number.isFinite(wi) || !Number.isFinite(ws)) return;
              const dist = Math.abs(ws - targetMs);
              if (dist < bestDist) {
                bestDist = dist;
                bestIdx = wi;
              }
            });
            if (Number.isFinite(bestIdx)) displayWordIndex = bestIdx;
          }
        }

        if (!Number.isFinite(displayWordIndex)) {
          displayWordIndex = Number(lineWordIndexes[0]);
        }
        if (!Number.isFinite(displayWordIndex)) return;

        const subtractionRaw = alert ? this.alertSubtractionPercent(alert) : null;
        const subtraction =
          Number.isFinite(Number(subtractionRaw))
            ? Math.abs(Number(subtractionRaw))
            : Math.max(
              0,
              100 - Number(perAlertBlock?.score_resta_computada_after ?? perAlertBlock?.scoreRestaComputadaAfter)
            );
        pills.push({
          alertId: String(alertId),
          alert,
          block: perAlertBlock || preferredBlock,
          subtraction: Number.isFinite(Number(subtraction)) ? Number(subtraction) : null,
          displayWordIndex,
        });
      });

      pills.sort((a, b) => {
        const at = Number(a?.alert?.gesture_timestamp_ms ?? a?.alert?.gestureTimestampMs);
        const bt = Number(b?.alert?.gesture_timestamp_ms ?? b?.alert?.gestureTimestampMs);
        if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at - bt;
        return String(a?.alertId ?? '').localeCompare(String(b?.alertId ?? ''));
      });

      const wordPills = pills.filter((pill) => Number(pill?.displayWordIndex) === idx);
      if (wordPills.length <= 1) return wordPills;
      return [this.aggregateWordPills(wordPills, idx)];
    },

    hasAlertPillsForWord(wordIndex, preferredBlock = null, line = null) {
      return this.alertPillsForWord(wordIndex, preferredBlock, line).length > 0;
    },

    aggregateWordPills(pills, displayWordIndex) {
      const safe = Array.isArray(pills) ? pills : [];
      const alertIds = safe
        .map((pill) => String(pill?.alertId ?? ''))
        .filter(Boolean);
      const rows = safe.map((pill) => {
        const subtraction = Number(pill?.subtraction);
        const safeSubtraction = Number.isFinite(subtraction) ? Math.abs(subtraction) : null;
        const coherence = Number(
          pill?.alert?.coherence_after ?? pill?.alert?.coherenceAfter
        );
        const safeCoherence = Number.isFinite(coherence) ? coherence : null;
        return { pill, subtraction: safeSubtraction, coherence: safeCoherence };
      });

      let aggregatedSubtraction = null;
      if (this.scoreTab === 'computed_subtraction') {
        const total = rows.reduce((sum, row) => {
          if (!Number.isFinite(row.subtraction)) return sum;
          return sum + Math.abs(Number(row.subtraction));
        }, 0);
        aggregatedSubtraction = Math.min(100, total);
      } else {
        // Alert Major mode: pick the alert with the worst coherence_after.
        // If coherence is missing, fall back to biggest subtraction.
        const withCoherence = rows.filter((row) => Number.isFinite(row.coherence));
        if (withCoherence.length) {
          withCoherence.sort((a, b) => Number(a.coherence) - Number(b.coherence));
          aggregatedSubtraction = Number(withCoherence[0]?.subtraction);
        } else {
          const reductions = rows
            .map((row) => Number(row.subtraction))
            .filter((n) => Number.isFinite(n));
          aggregatedSubtraction = reductions.length ? Math.max(...reductions) : null;
        }
      }

      return {
        alertId: null,
        alert: null,
        block: safe[0]?.block ?? null,
        subtraction: Number.isFinite(Number(aggregatedSubtraction))
          ? Number(aggregatedSubtraction)
          : null,
        displayWordIndex,
        alertIds,
        aggregated: true,
      };
    },

    // A gesture alert can be RAISED yet not subtract from coherence (e.g. the
    // semantic classifier found it compatible). reason_category is the stable
    // backend code for why; we fall back to the reduction for legacy data.
    alertReasonCategory(alert) {
      return String(alert?.reason_category || alert?.reasonCategory || '');
    },
    alertDidNotSubtract(alert) {
      if (!alert) return false;
      const cat = this.alertReasonCategory(alert);
      if (cat) return cat !== 'subtracted';
      const red = Number(alert?.coherence_reduction ?? alert?.coherenceReduction);
      return Number.isFinite(red) ? red <= 0 : false;
    },
    pillDidNotSubtract(pillAlert) {
      if (pillAlert?.aggregated) {
        const ids = Array.isArray(pillAlert?.alertIds) ? pillAlert.alertIds : [];
        if (!ids.length) return false;
        // Muted only when EVERY contributing alert failed to subtract.
        return ids.every((id) => {
          const a = this.alertIndexById.get(String(id));
          return a ? this.alertDidNotSubtract(a) : false;
        });
      }
      return this.alertDidNotSubtract(pillAlert?.alert);
    },
    // Potential reduction of the rule (what it WOULD subtract), regardless of
    // whether it applied. Used to show "-80%" on a muted, non-subtracting alert.
    alertPotentialReductionPct(alert) {
      const pct = Number(alert?.rule_reduction_pct ?? alert?.ruleReductionPct);
      if (Number.isFinite(pct) && pct > 0) return Math.round(pct);
      // Legacy fallback: if it actually subtracted, that IS the potential.
      const red = Number(alert?.coherence_reduction ?? alert?.coherenceReduction);
      return Number.isFinite(red) && red > 0 ? Math.round(red) : null;
    },
    pillPotentialReductionPct(pillAlert) {
      if (pillAlert?.aggregated) {
        const ids = Array.isArray(pillAlert?.alertIds) ? pillAlert.alertIds : [];
        const total = ids.reduce((sum, id) => {
          const a = this.alertIndexById.get(String(id));
          const pct = a ? this.alertPotentialReductionPct(a) : null;
          return Number.isFinite(pct) ? sum + pct : sum;
        }, 0);
        return total > 0 ? Math.min(100, total) : null;
      }
      return this.alertPotentialReductionPct(pillAlert?.alert);
    },
    reasonCategoryLabel(category) {
      const map = {
        subtracted: 'Subtracted',
        no_semantic_incompatibility: 'No semantic incompatibility',
        classifier_no_result: 'Classifier returned no result',
        no_semantics: 'No semantics (pause between ideas)',
        no_transcript_in_window: 'No transcript in time window',
        precondition_not_met: 'Speech precondition not met',
        rule_zero_reduction: 'Rule has zero reduction',
        no_rule_mapped: 'No rule mapped',
        rule_not_in_ruleset: 'Rule missing from ruleset',
      };
      return map[String(category || '')] || 'Not subtracting';
    },

    alertPillTone(pillAlert) {
      if (this.pillDidNotSubtract(pillAlert)) return 'muted';
      const percent = this.alertPillCoherencePercent(pillAlert);
      if (!Number.isFinite(percent)) return 'neutral';
      return this.toneFromCoherencePercent(percent);
    },

    alertPillStyle(pillAlert) {
      // Keep color semantics in CSS classes (success/warning/error/neutral).
      // Avoid overriding with inline red-only styles.
      return null;
    },

    alertPillCoherencePercent(pillAlert) {
      const subtraction = Number(pillAlert?.subtraction);
      if (Number.isFinite(subtraction)) {
        return Math.max(0, Math.min(100, 100 - Math.abs(subtraction)));
      }
      const after = Number(pillAlert?.alert?.coherence_after ?? pillAlert?.alert?.coherenceAfter);
      if (Number.isFinite(after)) {
        return Math.max(0, Math.min(100, after));
      }
      const blockScore = Number(this.blockScore(pillAlert?.block));
      if (Number.isFinite(blockScore)) {
        return Math.max(0, Math.min(100, blockScore));
      }
      return null;
    },

    errorScaleStyleFromSubtraction(subtractionRaw, backgroundAlpha = 1) {
      const subtraction = Number.isFinite(Number(subtractionRaw))
        ? Math.max(0, Math.min(100, Math.abs(Number(subtractionRaw))))
        : 0;
      const ratio = subtraction / 100;
      const lightness = 54 - ratio * 22;
      const borderLightness = Math.max(18, lightness - 12);
      const alpha = Number.isFinite(Number(backgroundAlpha))
        ? Math.max(0, Math.min(1, Number(backgroundAlpha)))
        : 1;
      return {
        backgroundColor: `hsl(0 72% ${lightness.toFixed(1)}% / ${alpha})`,
        borderColor: `hsl(0 78% ${borderLightness.toFixed(1)}%)`,
        color: '#ffffff',
      };
    },

    anchorUnderlineStyle(wordIndex, preferredBlock = null, line = null) {
      const pills = this.alertPillsForWord(wordIndex, preferredBlock, line);
      if (!Array.isArray(pills) || !pills.length) return null;
      // When every alert on this word was raised but didn't subtract, mute the
      // underline too so the word doesn't read as a scoring hit.
      if (pills.every((pill) => this.pillDidNotSubtract(pill))) {
        return { textDecorationColor: '#b0b3bd', textDecorationStyle: 'dotted' };
      }
      const mostSevere = pills.reduce((worst, pill) => {
        const current = Number(pill?.subtraction);
        const best = Number(worst?.subtraction);
        if (!Number.isFinite(best)) return pill;
        if (!Number.isFinite(current)) return worst;
        return current > best ? pill : worst;
      }, null);
      const percent = this.alertPillCoherencePercent(mostSevere);
      const tone = Number.isFinite(percent) ? this.toneFromCoherencePercent(percent) : 'neutral';
      const toneColor = {
        success: '#66bb6a',
        warning: '#ff9800',
        error: '#ef5350',
        neutral: '#c8cad2',
      };
      return {
        textDecorationColor: toneColor[tone] || toneColor.neutral,
      };
    },

    alertPillDigits(pillAlert) {
      // Raised-but-not-subtracting pills show the rule's POTENTIAL reduction in
      // grey, so it reads "would be -80%, but muted → not applied (compatible)".
      if (this.pillDidNotSubtract(pillAlert)) {
        const potential = this.pillPotentialReductionPct(pillAlert);
        return Number.isFinite(potential) ? `-${potential}%` : '·';
      }
      const subtraction = Number(pillAlert?.subtraction);
      if (!Number.isFinite(subtraction)) return '—';
      return `-${Math.round(Math.abs(subtraction))}%`;
    },

    alertPillTitle(pillAlert) {
      if (this.pillDidNotSubtract(pillAlert)) {
        const potential = this.pillPotentialReductionPct(pillAlert);
        const wouldBe = Number.isFinite(potential) ? `Would subtract -${potential}%, not applied` : 'Raised but not subtracting';
        if (pillAlert?.aggregated) {
          return `${wouldBe} — click for details`;
        }
        const cat = this.alertReasonCategory(pillAlert?.alert);
        return `${wouldBe}: ${this.reasonCategoryLabel(cat)} — click for details`;
      }
      const subtraction = Number(pillAlert?.subtraction);
      const label = Number.isFinite(subtraction) ? `-${Math.round(Math.abs(subtraction))}%` : '—';
      if (pillAlert?.aggregated) {
        const modeLabel =
          this.scoreTab === 'computed_subtraction'
            ? 'Word aggregate (subtraction mode)'
            : 'Word aggregate (alert major mode)';
        return `${modeLabel}: ${label}`;
      }
      return `Alert subtraction: ${label}`;
    },

    /**
     * Use the exact same block reference as the anchor pill value so color and number
     * never diverge (especially in merged mode where parent/child can differ).
     */
    anchorToneDisplayBlock(wordIndex) {
      return this.anchorScoreDisplayBlock(wordIndex);
    },

    isWordInHoveredAlertRange(wordIndex) {
      const idx = Number(wordIndex);
      if (!Number.isFinite(idx)) return false;
      return this.hoveredAlertRangeWordIndexes.has(idx);
    },

    isWordAlertVisuallyPersistent(wordIndex) {
      const idx = Number(wordIndex);
      if (!Number.isFinite(idx)) return false;
      return this.allAlertHighlightWordIndexes.has(idx) || this.allAlertAnchorWordIndexes.has(idx);
    },

    wordTokenClass(wordIndex) {
      const classes = [];
      const idx = Number(wordIndex);
      if (this.lastSeekWordIndex != null && idx === this.lastSeekWordIndex) {
        classes.push('words-live-token--seek-target');
      }
      if (this.allAlertHighlightWordIndexes.has(idx)) classes.push('words-live-token--highlight');
      if (this.allAlertAnchorWordIndexes.has(idx)) {
        classes.push('words-live-token--anchor');
        const anchorBlock = this.anchorToneDisplayBlock(idx);
        const anchorTone = this.alertLineTone(anchorBlock);
        classes.push(`words-live-token--anchor-${anchorTone}`);
      }
      if (this.activeBlockWordIndexes.has(idx)) {
        classes.push('words-live-token--active-block');
      }
      if (this.currentWordIndex != null && Number(wordIndex) === this.currentWordIndex) {
        classes.push('words-live-token--current');
        if (this.allAlertAnchorWordIndexes.has(idx)) {
          const b = this.anchorToneDisplayBlock(idx);
          const tone = b ? this.activeWordAlertTone(b) : 'neutral';
          classes.push(`words-live-token--current-solid-${tone}`);
        } else if (this.allAlertHighlightWordIndexes.has(idx)) {
          const segId = this.segmentBlockIdForWord(idx);
          const b = segId ? this.blockBySegmentKey(segId) : null;
          const tone = b ? this.activeWordAlertTone(b) : 'neutral';
          classes.push(`words-live-token--current-range-${tone}`);
        } else {
          classes.push('words-live-token--current-plain');
        }
      }
      return classes;
    },
    onWordActivate(word) {
      this.seekToWord(word);
    },

    onAlertPillActivate(wordIndex, pillAlert, preferredBlock = null) {
      const idx = Number(wordIndex);
      if (!Number.isFinite(idx)) return;
      const fallbackBlock = this.getPrimaryAnchorBlockForWordIndex(idx);
      const block = preferredBlock || fallbackBlock;
      if (!block) return;
      const alertId = pillAlert?.alertId != null ? String(pillAlert.alertId) : null;
      const alertIds = Array.isArray(pillAlert?.alertIds)
        ? pillAlert.alertIds.map((id) => String(id)).filter(Boolean)
        : null;
      this.openBlockModal(block, alertId, alertIds);
    },
    isWordRevealed(word) {
      const start = Number(word?.start_time_ms);
      if (!Number.isFinite(start)) return false;
      return start <= this.currentMs + this.wordTimingLeadMs;
    },

    contributingAlertIds(b) {
      const raw = b?.contributing_alert_ids ?? b?.contributingAlertIds;
      return Array.isArray(raw) ? raw.map(String) : [];
    },

    _meanCoherenceFromContributingIds(block) {
      const ids = this.contributingAlertIds(block);
      if (!ids.length) return 100;
      const values = ids
        .map((id) => this.alertIndexById.get(String(id)))
        .filter(Boolean)
        .map((a) => Number(a.coherence_after))
        .filter((n) => Number.isFinite(n));
      if (!values.length) return 100;
      return values.reduce((acc, n) => acc + n, 0) / values.length;
    },

    blockScore(b) {
      const ids = this.contributingAlertIds(b);
      const alerts = ids
        .map((id) => this.alertIndexById.get(String(id)))
        .filter(Boolean);
      if (alerts.length) {
        if (this.scoreTab === 'computed_subtraction') {
          const totalReduction = alerts.reduce((sum, a) => {
            const subtraction = Number(this.alertSubtractionPercent(a));
            if (!Number.isFinite(subtraction)) return sum;
            return sum + Math.max(0, Math.abs(subtraction));
          }, 0);
          return Math.max(0, 100 - totalReduction);
        }
        const worst = alerts.reduce((acc, a) => {
          const coherence = Number(a?.coherence_after ?? a?.coherenceAfter);
          if (!Number.isFinite(coherence)) return acc;
          return Math.min(acc, coherence);
        }, Number.POSITIVE_INFINITY);
        if (Number.isFinite(worst)) return worst;
      }
      return blockScoreForCoherenceMode(b, this.scoreTab);
    },

    coherenceVuetifyColor(percent) {
      const p = Number(percent);
      if (!Number.isFinite(p)) return 'grey darken-1';
      const g = Number(this.semaphoreThresholdGood);
      const w = Number(this.semaphoreThresholdWarn);
      const good = Number.isFinite(g) ? g : 80;
      const warn = Number.isFinite(w) ? w : 50;
      if (p >= good) return 'success';
      if (p >= warn) return 'warning';
      return 'error';
    },

    formatAggregateCoherence(p) {
      const n = Number(p);
      if (!Number.isFinite(n)) return '—';
      return `${Math.round(n)}%`;
    },

    formatMsClock(ms) {
      const s = Number(ms);
      if (!Number.isFinite(s) || s < 0) return '—';
      const totalSec = s / 1000;
      const m = Math.floor(totalSec / 60);
      const sec = Math.floor(totalSec % 60);
      const millis = Math.floor(s % 1000);
      return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}:${String(millis).padStart(3, '0')}`;
    },

    toneFromCoherencePercent(p) {
      const g = Number(this.semaphoreThresholdGood);
      const w = Number(this.semaphoreThresholdWarn);
      const good = Number.isFinite(g) ? g : 80;
      const warn = Number.isFinite(w) ? w : 50;
      const n = Number(p);
      if (!Number.isFinite(n)) return 'success';
      if (n >= good) return 'success';
      if (n >= warn) return 'warning';
      return 'error';
    },

    /**
     * Active word tone must match the alert representation shown in this panel
     * (same basis as chips/pills: current score mode via blockScore).
     */
    activeWordAlertTone(block) {
      if (!block) return 'neutral';
      return this.alertLineTone(block);
    },

    alertLineTone(block) {
      const score = Number(this.blockScore(block));
      return this.toneFromCoherencePercent(score);
    },

    /**
     * Below-threshold candidate flags for a displayed block. Uses the block's
     * own flags when present (engine-provided blocks); otherwise looks up the
     * fallback attribution map, where each flagged engine idea block is
     * assigned to exactly one displayed block (anchor word membership, or
     * single best time overlap when the anchor is missing). Idea mode rebuilds
     * blocks client-side, dropping the flags, so without single attribution a
     * flagged engine idea spanning several client phrases would flag all of
     * them.
     */
    blockBelowThresholdFlags(block) {
      if (!block) return { resta: false, alertMajor: false };
      const hasOwnFlags =
        block.below_threshold_sum_reduction !== undefined ||
        block.below_threshold_alert_major !== undefined;
      if (hasOwnFlags) {
        return {
          resta: block.below_threshold_sum_reduction === true,
          alertMajor: block.below_threshold_alert_major === true,
        };
      }
      const id = block.block_id ?? block.blockId;
      const fallback =
        id != null ? this.fallbackBelowThresholdFlagsByBlockId.get(String(id)) : null;
      return fallback
        ? { resta: fallback.resta, alertMajor: fallback.alertMajor }
        : { resta: false, alertMajor: false };
    },

    blockBelowThreshold(block) {
      const flags = this.blockBelowThresholdFlags(block);
      return flags.resta || flags.alertMajor;
    },

    blockBelowThresholdTooltip(block) {
      const flags = this.blockBelowThresholdFlags(block);
      const parts = [];
      if (flags.resta) parts.push('Below detection threshold (sum of reductions)');
      if (flags.alertMajor) parts.push('Below detection threshold (strongest alert)');
      return parts.join(' · ');
    },

    lyricsLineClass(idx) {
      const active = this.activeLyricsLineIndex;
      if (idx < active) return 'lyrics-line--past';
      if (idx === active) return 'lyrics-line--current';
      return 'lyrics-line--future';
    },

    seekToLyricsLine(line) {
      this.emitSeekSeconds(line.start_ms / 1000);
    },

    wordSeekTitle(word) {
      const ms = Number(word?.start_time_ms);
      if (!Number.isFinite(ms) || ms < 0) return 'Jump to this word';
      return `Jump to ${this.formatMsClock(ms)}`;
    },

    seekToWord(word) {
      const start = Number(word?.start_time_ms);
      if (!Number.isFinite(start) || start < 0) return;
      const wIdx = Number(word?.word_index);
      if (Number.isFinite(wIdx)) {
        this.lastSeekWordIndex = wIdx;
        window.clearTimeout(this._seekTargetClearTimer);
        this._seekTargetClearTimer = window.setTimeout(() => {
          if (this.lastSeekWordIndex === wIdx) this.lastSeekWordIndex = null;
        }, 2500);
      }
      this.emitSeekSeconds(Math.max(0, start) / 1000);
    },

    openBlockModal(b, alertId = null, alertIds = null) {
      this.modalBlock = b;
      this.modalAlertId = alertId != null ? String(alertId) : null;
      this.modalAlertIds =
        Array.isArray(alertIds) && alertIds.length
          ? alertIds.map((id) => String(id)).filter(Boolean)
          : null;
      this.blockModalOpen = true;
    },

    closeBlockModal() {
      this.blockModalOpen = false;
      this.modalBlock = null;
      this.modalAlertId = null;
      this.modalAlertIds = null;
      this.alertFieldsHelpOpen = false;
      this.expandedMatchedTextByKey = {};
    },

    alertDisplayKey(alert, index) {
      const id = alert?.alert_id ?? alert?.alertId;
      return id != null ? String(id) : `idx-${index}`;
    },

    isMatchedTextExpanded(alert, index) {
      const key = this.alertDisplayKey(alert, index);
      return Boolean(this.expandedMatchedTextByKey[key]);
    },

    toggleMatchedTextExpanded(alert, index) {
      const key = this.alertDisplayKey(alert, index);
      this.$set(this.expandedMatchedTextByKey, key, !this.isMatchedTextExpanded(alert, index));
    },

    shouldShowMatchedTextToggle(alert) {
      const text = this.alertSemanticText(alert);
      if (!text) return false;
      const normalized = String(text).trim();
      return normalized.length > 120 || normalized.includes('\n');
    },

    _alertMetadata(alert) {
      const m = alert?.metadata;
      return m && typeof m === 'object' && !Array.isArray(m) ? m : {};
    },

    _alertSemantic(alert) {
      // Source of truth: data.coherence_analysis.coherence_alerts[].semantic_match
      // (alerts arrive in this component through `coherenceAlerts` prop).
      const sm = alert?.semantic_match;
      return sm && typeof sm === 'object' ? sm : null;
    },

    alertGestureName(alert) {
      const v = alert?.gesture_name ?? alert?.gestureName;
      if (v == null) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    },

    alertMetadataWindowSeconds(alert) {
      const m = this._alertMetadata(alert);
      const raw = m.window_seconds ?? m.window_second ?? m.windowSeconds ?? m.windowSecond;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    },

    alertMetadataSourceValue(alert) {
      const m = this._alertMetadata(alert);
      const v = m.source_value ?? m.sourceValue;
      if (v == null) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    },

    alertSubtractionPercent(alert) {
      const before = Number(alert?.coherence_before ?? alert?.coherenceBefore);
      const after = Number(alert?.coherence_after ?? alert?.coherenceAfter);
      if (Number.isFinite(before) && Number.isFinite(after)) {
        return before - after;
      }
      const meta = this._alertMetadata(alert);
      const fallback = Number(
        meta?.score_resta_computada ??
          meta?.scoreRestaComputada ??
          meta?.subtraction ??
          meta?.delta
      );
      return Number.isFinite(fallback) ? fallback : null;
    },

    formatAlertSubtractionChip(alert) {
      const rest = this.alertSubtractionPercent(alert);
      if (!Number.isFinite(rest)) return '—';
      return `-${Math.round(Math.abs(rest))}%`;
    },

    alertSemanticSpeaker(alert) {
      const sm = this._alertSemantic(alert);
      if (!sm) return null;
      const v = sm.speaker;
      if (v == null) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    },

    alertSemanticText(alert) {
      const sm = this._alertSemantic(alert);
      if (!sm) return null;
      const v = sm.text;
      if (v == null) return null;
      const s = String(v).trim();
      return s === '' ? null : s;
    },

    formatAlertFieldDisplay(value) {
      if (value == null) return '—';
      const s = String(value).trim();
      return s === '' ? '—' : s;
    },

    formatAlertWindowSecondsDisplay(alert) {
      const n = this.alertMetadataWindowSeconds(alert);
      if (n == null) return '—';
      return `${n} s`;
    },

    alertAnchorSeconds(alert) {
      const ts = Number(alert?.gesture_timestamp_ms);
      if (Number.isFinite(ts) && ts >= 0) return ts / 1000;
      return 0;
    },

    seekToAlert(a) {
      this.emitSeekSeconds(this.alertAnchorSeconds(a));
    },

    /** Jump to an alert without semantics and open its details (its own per-alert block). */
    openNoSemanticsAlert(a) {
      const id = String(a?.alert_id ?? a?.alertId ?? '');
      this.seekToAlert(a);
      const block = this.perAlertBlockByAlertId.get(id);
      if (block) this.openBlockModal(block, id);
    },

    emitSeekSeconds(seconds) {
      if (!Number.isFinite(seconds)) return;
      this.$emit('jump-to-time', seconds);
      if (this.interviewId != null) {
        this.requestInterviewVideoSeek({ analysisId: this.interviewId, seconds });
      }
    },
  },
};
</script>

<style scoped>
.coherence-semantics-card {
  --semantics-panel-surface-rgb: 5, 6, 9;
  --semantics-panel-surface-alpha: 0.1;
  border-top: 3px solid #6200ea;
  min-width: 300px;
  max-width: 320px;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  /* Fallback when backdrop-filter is unavailable */
  background: rgba(var(--semantics-panel-surface-rgb), var(--semantics-panel-surface-alpha)) !important;
  pointer-events: auto;
}

@supports ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .coherence-semantics-card {
    --semantics-panel-surface-rgb: 4, 5, 8;
    --semantics-panel-surface-alpha: 0.9;
    /* Dark veil + blur: strong enough that the panel never reads as “white” */
    background: rgba(var(--semantics-panel-surface-rgb), var(--semantics-panel-surface-alpha)) !important;
    -webkit-backdrop-filter: saturate(140%) blur(22px);
    backdrop-filter: saturate(140%) blur(22px);
  }
}

.semantics-card-title-row {
  min-width: 0;
  width: 100%;
}

.semantics-card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Same role as gesture-meta: label at the right of the title row */
.semantics-card-header-meta {
  flex: 0 1 auto;
  min-width: 0;
  color: rgba(235, 236, 240, 0.58) !important;
}

.semantics-card-meta--ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.semantics-option-value {
  color: rgba(235, 236, 240, 0.68);
}

.semantics-text-size-control {
  min-height: 32px;
}

.coherence-semantics-card--compact-lyrics .lyrics-panel {
  min-height: 0 !important;
}

.coherence-semantics-card--compact-lyrics .semantics-scroll-fade {
  display: none;
}

.semantics-text-size-title {
  color: rgba(0, 0, 0, 0.87);
}

.semantics-text-size-a {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 700;
  line-height: 1;
  user-select: none;
}

.semantics-text-size-a--small {
  font-size: 12px;
}

.semantics-text-size-a--large {
  font-size: 20px;
}

.coherence-semantics-card--full {
  max-width: none;
  width: 100%;
}

.lyrics-card-text.semantics-card-text--compact-lyrics {
  flex: 0 0 auto !important;
  min-height: 0 !important;
}

.lyrics-card-text.semantics-card-text--compact-lyrics .semantics-scroll-shell {
  flex: 0 0 auto;
  min-height: 0;
}

.semantics-scroll {
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE / legacy Edge */
}

.semantics-scroll::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Chromium */
}

.lyrics-card-text {
  background: transparent;
  padding: 0 !important;
}

.semantics-scroll--lyrics {
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: auto;
  scrollbar-width: none;
}

.semantics-scroll--lyrics::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.semantics-scroll-shell {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.semantics-scroll-fade {
  position: absolute;
  left: 0;
  right: 0;
  height: 42px;
  z-index: 2;
  pointer-events: none;
}

.semantics-scroll-fade--top {
  top: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--semantics-panel-surface-rgb), calc(var(--semantics-panel-surface-alpha) * 1.35)) 0%,
    rgba(var(--semantics-panel-surface-rgb), 0) 100%
  );
  transform: scaleX(-1);
}

.semantics-scroll-fade--bottom {
  bottom: 0;
  background: linear-gradient(
    0deg,
    rgba(var(--semantics-panel-surface-rgb), calc(var(--semantics-panel-surface-alpha) * 1.35)) 0%,
    rgba(var(--semantics-panel-surface-rgb), 0) 100%
  );
  transform: scaleX(-1);
}

.lyrics-empty {
  color: rgba(232, 233, 238, 0.5);
}

.lyrics-panel {
  padding: 0 12px;
  min-height: 220px;
  --semantics-font-scale: 0.86;
}

.lyrics-edge-spacer {
  width: 100%;
  pointer-events: none;
}

.lyrics-line-wrap {
  text-align: center;
  margin: 4px auto;
  max-width: 28rem;
  cursor: pointer;
  border-radius: 12px;
  padding: 12px 8px;
  transition: opacity 0.45s cubic-bezier(0.22, 0.61, 0.36, 1);
  outline: none;
}

.lyrics-segment-card {
  border-radius: 10px;
  padding: 12px 14px 14px;
  border: 2px solid transparent;
  transition: border-color 0.2s ease;
}

/* Active in time: border in the card's own tone color. */
.lyrics-segment-card--active.lyrics-segment-card--success {
  border-color: rgba(46, 125, 50, 0.95);
}
.lyrics-segment-card--active.lyrics-segment-card--neutral {
  border-color: rgba(180, 180, 192, 0.8);
}
.lyrics-segment-card--active.lyrics-segment-card--warning {
  border-color: rgba(245, 124, 0, 0.95);
}
.lyrics-segment-card--active.lyrics-segment-card--error {
  border-color: rgba(198, 40, 40, 0.95);
}

/* Labeled overrides the active border color (background untouched). */
.lyrics-segment-card--active.lyrics-segment-card--labeled-deceptive {
  border-color: rgba(76, 175, 80, 0.95);
}
.lyrics-segment-card--active.lyrics-segment-card--labeled-truthful {
  border-color: rgba(244, 67, 54, 0.95);
}

/* Below the detection threshold: subtle amber edge marking a candidate idea */
.lyrics-segment-card--below-threshold {
  box-shadow: inset 3px 0 0 0 rgba(255, 193, 7, 0.7);
}

.segment-below-threshold-flag {
  cursor: help;
}

.lyrics-segment-card__time {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.85);
}

.lyrics-segment-card__speaker {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.lyrics-segment-card__header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.lyrics-segment-card__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.label-btn {
  font-size: 11px !important;
  letter-spacing: 0.02em;
  text-transform: none !important;
  opacity: 0.6;
  transition: opacity 0.15s, background-color 0.15s;
}

/* Debug actions (legacy labeling): removed from the layout until the
   containing card is hovered (no reserved gap in the card), and only on
   devices that support hover. */
.debug-actions {
  display: none;
}

@media (hover: hover) {
  .lyrics-segment-card:hover .debug-actions,
  .lyrics-segment-card:focus-within .debug-actions {
    display: flex;
  }
}

/* Bottom action bar, mirrors the gesture panel footer (.gesture-timeline-footer). */
.semantics-panel-footer {
  display: flex;
  align-items: center;
}

.label-btn--active-correct {
  opacity: 1 !important;
  color: #fff !important;
}

.label-btn--active-incorrect {
  opacity: 1 !important;
  color: #fff !important;
}

.label-btn--inactive:hover {
  opacity: 0.9;
}

.lyrics-plain-line-wrap {
  position: relative;
  border-radius: 6px;
  transition: background-color 0.15s;
}

.lyrics-plain-line-wrap--fn {
  border: 1px dashed rgba(244, 67, 54, 0.5);
  border-radius: 6px;
  padding: 2px 4px;
}

.fn-label-row {
  display: flex;
  padding: 2px 0 4px;
}

.fn-btn-fade-enter-active,
.fn-btn-fade-leave-active {
  transition: opacity 0.15s;
}
.fn-btn-fade-enter,
.fn-btn-fade-leave-to {
  opacity: 0;
}

.segment-score-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.38);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.segment-score-circle--success {
  background: #2e7d32;
  border-color: #1b5e20;
}

.segment-score-circle--warning {
  background: #ef6c00;
  border-color: #e65100;
}

.segment-score-circle--error {
  background: #c62828;
  border-color: #b71c1c;
}

.lyrics-segment-card--success {
  background: rgba(46, 125, 50, 0.2);
}

.lyrics-segment-card--neutral {
  background: rgba(92, 92, 104, 0.24);
}

.lyrics-segment-card--warning {
  background: rgba(245, 124, 0, 0.22);
}

.lyrics-segment-card--error {
  background: rgba(198, 40, 40, 0.26);
}

.lyrics-line-wrap:focus-visible {
  box-shadow: 0 0 0 2px rgba(98, 0, 234, 0.55);
}

.lyrics-line {
  display: inline-block;
  font-size: calc(24px * var(--semantics-font-scale));
  line-height: 1.45;
  font-weight: 400;
  transition: color 0.45s cubic-bezier(0.22, 0.61, 0.36, 1);
  transform: none;
  /* Match one lyric row including segment run vertical padding (2px + 2px). */
  --lyrics-active-min-height: calc(1.45em + 4px);
}

.lyrics-line--past {
  color: rgba(210, 212, 220, 0.38);
  transform: none;
}

.lyrics-line--future {
  color: rgba(220, 222, 230, 0.48);
  transform: none;
}

.lyrics-line--current {
  color: rgba(245, 246, 250, 0.96);
  transform: none;
}

/* One outline per semantic segment: consecutive words of the same block share a wrapper. */
.lyrics-segment-run--plain {
  display: contents;
}

.lyrics-segment-run--outlined {
  display: inline;
  border: none;
  border-radius: 5px;
  padding: 2px 4px;
  margin: 0 2px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  vertical-align: baseline;
}

.lyrics-segment-run--outlined.lyrics-segment-run--tone-success {
  background: rgba(46, 125, 50, 0.22);
}

.lyrics-segment-run--outlined.lyrics-segment-run--tone-neutral {
  background: rgba(92, 92, 104, 0.28);
}

.lyrics-segment-run--outlined.lyrics-segment-run--tone-warning {
  background: rgba(245, 124, 0, 0.26);
}

.lyrics-segment-run--outlined.lyrics-segment-run--tone-error {
  background: rgba(198, 40, 40, 0.32);
}

/* Segment spans multiple lyric lines: drop closed corners so the block reads as one strip. */
.lyrics-segment-run--outlined.lyrics-segment-run--bridge-left {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: 0;
}

.lyrics-segment-run--outlined.lyrics-segment-run--bridge-right {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin-right: 0;
}

.lyrics-panel .words-live-token--lyrics {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  width: var(--lyrics-token-lock-width, auto);
  min-width: var(--lyrics-token-lock-width, auto);
  max-width: var(--lyrics-token-lock-width, none);
  min-height: var(--lyrics-active-min-height);
  margin: 0 1px;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid transparent;
  box-sizing: border-box;
  font-weight: 500;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.lyrics-panel .words-live-token--lyrics.words-live-token--spoken {
  color: rgba(250, 251, 255, 0.98);
  font-weight: 700;
  opacity: 1;
}

.lyrics-panel .words-live-token--lyrics.words-live-token--spoken:not(.words-live-token--current) {
  text-shadow: none !important;
  filter: none !important;
  animation: none !important;
}

.lyrics-panel .words-live-token--lyrics:not(.words-live-token--current):hover {
  background-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.lyrics-panel .words-live-token--lyrics.words-live-token--current:hover {
  filter: none;
}

.lyrics-panel .words-live-token--lyrics:focus {
  outline: none;
}

.lyrics-panel .words-live-token--lyrics:focus-visible {
  background-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 2px rgba(98, 0, 234, 0.65);
}

.lyrics-panel .words-live-token--pending {
  opacity: 0.62;
  color: rgba(196, 202, 214, 0.96) !important;
  text-shadow: none !important;
  filter: saturate(0.22) contrast(0.96) brightness(0.98);
}

/* Base block in transcript: contour only (no fill). */
.lyrics-panel .words-live-token--highlight {
  background: transparent;
  text-decoration: none;
  border: 1px solid rgba(224, 227, 236, 0.35);
  border-radius: 5px;
  box-shadow: none;
}

.lyrics-panel .words-live-token--anchor {
  background: transparent;
  box-shadow: none;
  border: 1px solid transparent;
}

.lyrics-panel .words-live-token--anchor-with-pill {
  display: inline;
  vertical-align: baseline;
  white-space: normal;
}

/* Anchor tokens with pills must flow like plain inline text, not boxed multi-line blocks. */
.lyrics-panel .words-live-token--anchor-with-pill {
  width: auto !important;
  min-width: 0 !important;
  max-width: none !important;
  min-height: 0 !important;
}

.lyrics-panel .words-live-token--in-segment-card.words-live-token--highlight,
.lyrics-panel .words-live-token--in-segment-card.words-live-token--active-block {
  border: none;
  background: transparent;
  box-shadow: none;
}

.lyrics-panel .words-live-token__anchor-text {
  text-decoration: underline;
  text-decoration-thickness: 3px;
  text-underline-offset: 0.15em;
}

.lyrics-panel .words-live-token--anchor.words-live-token--anchor-warning .words-live-token__anchor-text {
  text-decoration-color: #ff9800;
}

.lyrics-panel .words-live-token--anchor.words-live-token--anchor-error .words-live-token__anchor-text {
  text-decoration-color: #ef5350;
}

.lyrics-panel .words-live-token--anchor.words-live-token--anchor-success .words-live-token__anchor-text {
  text-decoration-color: #66bb6a;
}

.lyrics-panel .words-live-token--anchor.words-live-token--anchor-neutral .words-live-token__anchor-text {
  text-decoration-color: #c8cad2;
}

.lyrics-panel .anchor-coherence-pill {
  pointer-events: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  width: 34px;
  max-width: 34px;
  height: 24px;
  padding: 0;
  border-radius: 8px;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  border: 1px solid rgba(255, 255, 255, 0.26);
  box-sizing: border-box;
  cursor: pointer;
  margin-left: 0;
  vertical-align: middle;
}

.lyrics-panel .words-live-token--anchor-with-pill .anchor-coherence-pill {
  margin-left: 4px;
}

.lyrics-panel .anchor-coherence-pill:focus {
  outline: none;
}

.lyrics-panel .anchor-coherence-pill:focus-visible {
  box-shadow: 0 0 0 2px rgba(100, 181, 246, 0.55);
}

.lyrics-panel .anchor-coherence-pill__value {
  display: inline-flex;
}

.lyrics-panel .anchor-coherence-pill__info {
  display: none;
  font-size: 13px !important;
}

.lyrics-panel .anchor-coherence-pill:hover .anchor-coherence-pill__value,
.lyrics-panel .anchor-coherence-pill:focus-visible .anchor-coherence-pill__value {
  display: none;
}

.lyrics-panel .anchor-coherence-pill:hover .anchor-coherence-pill__info,
.lyrics-panel .anchor-coherence-pill:focus-visible .anchor-coherence-pill__info {
  display: inline-flex;
}

.lyrics-panel .anchor-coherence-pill--success {
  background: rgba(46, 125, 50, 0.42);
  color: #ffffff;
  border-color: rgba(76, 175, 80, 0.58);
}

.lyrics-panel .anchor-coherence-pill--neutral {
  background: rgba(92, 92, 104, 0.42);
  color: #fafafa;
  border-color: rgba(122, 122, 136, 0.58);
}

.lyrics-panel .anchor-coherence-pill--warning {
  background: rgba(239, 108, 0, 0.42);
  color: #ffffff;
  border-color: rgba(245, 124, 0, 0.6);
}

.lyrics-panel .anchor-coherence-pill--error {
  background: rgba(198, 40, 40, 0.46);
  color: #ffffff;
  border-color: rgba(239, 83, 80, 0.64);
}

/* Raised but not subtracting: muted, dashed, still clickable. */
.lyrics-panel .anchor-coherence-pill--muted {
  background: rgba(120, 124, 138, 0.18);
  color: rgba(255, 255, 255, 0.55);
  border-color: rgba(150, 154, 168, 0.4);
  border-style: dashed;
  opacity: 0.7;
}

.lyrics-panel .anchor-coherence-pill--muted:hover,
.lyrics-panel .anchor-coherence-pill--muted:focus-visible {
  opacity: 1;
}

.lyrics-panel .words-live-token--active-block {
  background: transparent;
  border: 1px solid rgba(224, 227, 236, 0.35);
  border-radius: 5px;
}

/* Inside a segment wrapper, borders/fills live on the run — not on each token. */
.lyrics-panel .lyrics-segment-run--outlined .words-live-token--highlight,
.lyrics-panel .lyrics-segment-run--outlined .words-live-token--active-block {
  border: none;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

/* Inside the same block-card, tokens must not create inner gaps. */
.lyrics-panel .lyrics-segment-run--outlined .words-live-token--lyrics {
  margin-left: 0;
  margin-right: 0;
}

/* Hover over any alert-linked word (anchor/context) to preview the whole unified range. */
.lyrics-panel .words-live-token--range-hover {
  background: transparent;
  text-decoration: underline;
  text-decoration-thickness: 3px;
  text-decoration-color: #8b5cf6;
  text-underline-offset: 0.15em;
}

.lyrics-panel .words-live-token--anchor-with-pill.words-live-token--range-hover {
  text-decoration: none;
}

.lyrics-panel .words-live-token--anchor-with-pill.words-live-token--range-hover .words-live-token__anchor-text {
  text-decoration: underline;
  text-decoration-thickness: 3px;
  text-decoration-color: #8b5cf6;
  text-underline-offset: 0.15em;
}

/* Brief highlight after clicking a word until playhead lands inside that word. */
.lyrics-panel .words-live-token--lyrics.words-live-token--seek-target {
  outline: 2px solid rgba(100, 181, 246, 0.95);
  outline-offset: 2px;
  border-radius: 2px;
}

.lyrics-panel .words-live-token--highlight:hover,
.lyrics-panel .words-live-token--anchor:hover,
.lyrics-panel .words-live-token--active-block:hover,
.lyrics-panel .words-live-token--range-hover:hover {
  background-color: transparent;
  box-shadow: none;
}

/* Active word: layout + rounded corners; background comes from plain / range / solid modifiers. */
.lyrics-panel .words-live-token--lyrics.words-live-token--current {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  color: #ffffff;
  font-weight: 800;
  text-shadow: none;
  transform-origin: center;
  animation: active-word-flash 500ms ease-out 1 forwards;
  transition: none;
}

.lyrics-panel .lyrics-line .words-live-token--lyrics.words-live-token--current {
  border-radius: 0;
}

.lyrics-panel .lyrics-line .words-live-token--lyrics.words-live-token--anchor-with-pill.words-live-token--current {
  justify-content: center;
}

/* No alert: very light dark veil, no border. */
.lyrics-panel .words-live-token--current.words-live-token--current-plain {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
  box-shadow: none;
}

/* Anchor (alert word): solid fill per severity. */
.lyrics-panel .words-live-token--current.words-live-token--current-solid-neutral {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

.lyrics-panel .words-live-token--current.words-live-token--current-solid-success {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

.lyrics-panel .words-live-token--current.words-live-token--current-solid-warning {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

.lyrics-panel .words-live-token--current.words-live-token--current-solid-error {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

/* Highlight range only (not anchor): tinted alert color. */
.lyrics-panel .words-live-token--current.words-live-token--current-range-neutral {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

.lyrics-panel .words-live-token--current.words-live-token--current-range-success {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

.lyrics-panel .words-live-token--current.words-live-token--current-range-warning {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

.lyrics-panel .words-live-token--current.words-live-token--current-range-error {
  background: transparent;
  color: #ffffff;
  border-color: transparent;
}

@keyframes active-word-flash {
  0% {
    text-shadow:
      0 0 3px rgba(255, 255, 255, 0.5),
      0 0 7px rgba(255, 255, 255, 0.3),
      0 0 12px rgba(255, 255, 255, 0.18);
    filter: brightness(1.06);
    transform: scale(1.003);
  }
  100% {
    text-shadow: none;
    filter: none;
    transform: scale(1);
  }
}

.lyrics-panel .words-live-token--anchor-with-pill.words-live-token--current .anchor-coherence-pill {
  border-color: rgba(255, 255, 255, 0.55);
}

.alert-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alert-detail-row {
  display: grid;
  grid-template-columns: minmax(7.5rem, 34%) 1fr;
  gap: 10px 12px;
  align-items: start;
  color: rgba(37, 43, 52, 0.9);
}

.alert-detail-row--text .alert-detail-value {
  white-space: pre-wrap;
  word-break: break-word;
}

.alert-detail-label {
  font-weight: 600;
  color: rgba(66, 73, 82, 0.78);
  text-transform: none;
  letter-spacing: 0.01em;
}

.alert-detail-value {
  color: rgba(24, 29, 37, 0.92);
}

.alert-detail-value--matched-text {
  display: block;
}

.alert-matched-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
}

.alert-matched-text--expanded {
  display: block;
  -webkit-line-clamp: initial;
  overflow: visible;
}

.alert-matched-text-toggle {
  display: inline-block;
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3f51b5;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
}

.alert-matched-text-toggle:hover {
  text-decoration: underline;
}

.semantics-dialog-card {
  color: rgba(24, 29, 37, 0.94);
}

.semantics-dialog-alert-card {
  border-color: rgba(16, 24, 40, 0.12) !important;
}

.alert-help-block:last-child {
  margin-bottom: 0 !important;
}

.coherence-bar-wrap {
  width: 100%;
  padding: 0 6px;
  box-sizing: border-box;
}

.coherence-bar {
  position: relative;
  height: 26px;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.coherence-base {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: rgba(0, 0, 0, 0.35);
}

.coherence-gradient {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #c4e1cf 0%, #edd6b3 50%, #efbcc4 100%);
}

.bar-indicator {
  position: absolute;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  transform: translateX(-50%) scale(1);
  transform-origin: center;
  transition:
    left 0.35s ease-out,
    opacity 0.22s ease,
    transform 0.22s ease;
  z-index: 3;
}

.coherence-indicator-fade-enter-active,
.coherence-indicator-fade-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.coherence-indicator-fade-enter,
.coherence-indicator-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) scale(0.86);
}

.indicator-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.98);
  color: #3a3a3a;
  font-size: 11px;
  font-weight: 700;
  line-height: normal;
  border: none;
}

.indicator-pill.pill-good {
  background: #2e7d32;
  color: #fff;
}

.indicator-pill.pill-warn {
  background: #f57c00;
  color: #fff;
}

.indicator-pill.pill-bad {
  background: #c62828;
  color: #fff;
}

.bar-labels {
  font-size: 11px;
  color: rgba(225, 227, 233, 0.55);
}

.words-live-token {
  display: inline-block;
  margin-bottom: 1px;
  padding: 0 2px;
  border-radius: 3px;
  transition: opacity 0.2s ease;
}

.words-live-token--pending {
  opacity: 0.35;
}

.words-live-token--highlight {
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-decoration-color: #f57c00;
}

.words-live-token--anchor {
  background: rgba(198, 40, 40, 0.15);
  text-decoration: underline;
  text-decoration-color: #c62828;
}

/* Default "now playing" tint only outside lyrics panel (lyrics use plain / range / solid current-*). */
.words-live-token--current:not(.words-live-token--lyrics) {
  background: rgba(46, 125, 50, 0.2);
}

/* Alerts raised outside every idea: listed apart, never scored against an idea. */
.no-semantics-strip {
  flex-shrink: 0;
}

.no-semantics-strip__hint {
  opacity: 0.7;
}

.no-semantics-strip__chips {
  max-height: 72px;
  overflow-y: auto;
}
</style>
